// M10-12366
import { Transition } from "@headlessui/react";
import { MessageCircle } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import downArrow from "src/assets/down-arrow.svg";
import starEmpty from "src/assets/starEmpty.svg";
import SuccessIcon from "src/assets/successCircle.svg";
import { Autocomplete, Button, Checkbox, Input, Modal, Spinner } from "src/components";
import VersioningCrate from "src/components/Versioning/versioningModal.component";
import { invoiceActions } from "src/context/invoice-slice";
import { RootState } from "src/context/store";
import Download from "src/features/fields/assets/Download copy.svg";
import star from "src/features/fields/assets/star.svg";
import { useEventStatus } from "src/hooks/useEventStatus";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { useToast } from "src/hooks/useToast";
import {
    downloadInvoice,
    getInvoiceBatchList,
    getInvoiceDetails,
    getInvoicePriceList,
    getOrderedProducts,
    getSimilarCompanyProducts,
    getSimilarOrders,
    postInvoicePackChange,
    reEditInvoice,
} from "src/services/invoice-api";
import Book from "../../assets/book.png";
import PurpleLeftArrowIcon from "../../assets/purple-left-arrow.svg";
import { DEFAULT_TABLE_DATA, getInvoiceDetailsInfo, isPriceExceedingPtr } from "../invoice-master/invoice-helper"; // M10-14735 M10-15166
import InvoiceLayout from "../invoice-master/invoice-layout.component";
import InvoiceDetailCard from "./invoice-detail.component";
import InvoiceApprovalOrderedData from "./InvoiceApprovalCards/ApprovalOrderedData";
import PerStripOrderEdit, { packEquivalentPrice } from "./InvoiceApprovalCards/PerStripOrderEdit";
import InvoiceRemarks from "./InvoiceRemarks";
import PartialIcon from "src/assets/partial.svg";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import usePermission from "src/hooks/usePermission";
import useDebounceLeading from "src/hooks/useDebounceLeading";
import InvoiceEntryForm from "./InvoiceEntryCards/InvoiceEntryForm";
import { generateUniqueID } from "src/constants/functions";
import { useForm } from "react-hook-form";
import { priceIdentifierList } from "./invoice-constants";
import { schema } from "./invoice-constants";
import { yupResolver } from "@hookform/resolvers/yup";
import IsDifferentOrder from "./InvoiceApprovalCards/IsDifferentOrder";


const subtleColors = ["#F9F9FF", "#EFF8FF", "#F3FFF4", "#FFF5F5", "#FDFCF7", "#F3F0FF", "#EFFFFA", "#FFF3F7", "#F0FBFF", "#F7F5FF"];

interface selectedPriceIdentifier {
    key: string;
    description: string;
    priceldentifier_id: number;
}

export const InvoiceReEntryScreen = () => {
    const { stockist_invoice_attachment_id } = useParams();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const { invoiceApprovalData, tempDate, invoiceDetails } = useSelector((state: RootState) => state.invoice); //M10-12020
    const [mappedData, setMappedData] = useState<any[]>([]);
    const [selectedItemDetails, setSelectedItemDetails] = useState<any>(null);
    const [tableData, setTableData] = useState(DEFAULT_TABLE_DATA);
    const { toast } = useToast();
    const [productsData, setProductsData] = useState<any[]>([]);
    const [orderedData, setOrderedData] = useState<any[]>([]);
    const [similarOrders, setSimilarOrders] = useState<any[]>([]);
    const [tcs, setTcs] = useState<number | any>(0);
    const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
    const navigate = useNavigate();
    const [isRemarksShown, setIsRemarksShown] = useState(false);
    const [showOrderDropdown, setShowOrderDropdown] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [VersioningModal, setVersioningModal] = useState(false);
    const [remainingQuantity, setRemainingQuantity] = useState<any[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
    const [checkedRows, setCheckedRows] = useState<Set<string>>(new Set());
    const [isExpanded, setIsExpanded] = useState(true);
    const [isMapped, setIsMapped] = useState(true); //M10-11117
    const [isDifferentOrderModalOpen, setIsDifferentOrderModalOpen] = useState<boolean>(false);
    const [orderToSelect, setOrderToSelect] = useState<any[]>([]);
    const [packChangeMessage, setPackChangeMessage] = useState<string>("");
    const [openPerStripModal, setOpenPerStripModal] = useState<boolean>(false);
    // Runs the pack change once its popup is answered (true = Yes).
    const perStripContinueRef = useRef<((confirmed: boolean) => void) | null>(null);
    // One-shot: the modal can fire close and decline for the same answer.
    const resumePerStripSave = (confirmed: boolean) => {
      const resume = perStripContinueRef.current;
      perStripContinueRef.current = null;
      setOpenPerStripModal(false);
      resume?.(confirmed);
    };

    //M10-9862
    const location = useLocation();
    const { CompanyCode: companyCodeFromState } = location.state || {};
    // Fall back to localStorage when opened in a new tab (location.state is lost).
    const CompanyCode = companyCodeFromState || window.localStorage.getItem("company_code");
    const company_code = invoiceApprovalData?.company_code;
    const { hasPermission: viewRemarks } = usePermission({
        module: fineGrainedPermissionsKeys.INVOICE.VIEWREMARKS.module,
        action: fineGrainedPermissionsKeys.INVOICE.VIEWREMARKS.action,
    });

    const getRowId = (item: any, index: number) => `${item.unique_key}-${index}`;

    // Same product, different batches: every other batch checked in the "Select" column for the
    // product currently being viewed rides along as its own line item, sharing the pricing decided
    // in the panel below. Computed here (render scope, not just inside handleSaveModifiedData) so a
    // read-only preview of each sibling batch can be shown on screen before Save, and reused by
    // handleSaveModifiedData so what gets saved always matches what was previewed. Scheme orders
    // never bulk-map — even if boxes were checked before a Scheme order was chosen, the Select
    // checkboxes are only disabled going forward, so this is the backstop.
    const siblingBatches = selectedOrders?.[0]?.price_identifier === 2
        ? []
        : selectedProducts.filter(
            (p) => p.item_code === selectedItemDetails?.item_code && p.unique_key !== selectedItemDetails?.unique_key
        );
    const isBulkBatchMapping = siblingBatches.length > 0;
    const finalDiscountPerc =
        isBulkBatchMapping && selectedOrders?.[0]?.price_identifier === 1
            ? Number(selectedOrders?.[0]?.discount_on_ptr) || 0
            : Number(tableData.find((item) => item.type === "Discount %")?.modifiedData);

    // M10-15357: when multiple batches of the same item are checked in the "Select" column, the
    // "Select an Item" panel's Invoice Data column should reflect what was invoiced across all of
    // them combined (Quantity/Scheme Qty summed, Discount % amount-weighted), not just the single
    // batch that happened to open the panel. Display-only — derived from selectedItemDetails +
    // siblingBatches, so the underlying invoice rows, Modified Data, and the separate-line-item
    // Save logic are untouched.
    const combinedInvoiceBatches = selectedItemDetails ? [selectedItemDetails, ...siblingBatches] : [];
    const combinedInvoiceQuantity = combinedInvoiceBatches.reduce((sum, b) => sum + (Number(b?.quantity) || 0), 0);
    const combinedInvoiceSchemeQty = combinedInvoiceBatches.reduce((sum, b) => sum + (Number(b?.free_qty) || 0), 0);
    const combinedInvoiceGrossAmt = combinedInvoiceBatches.reduce((sum, b) => sum + (Number(b?.gross_amt) || 0), 0);
    const combinedInvoiceDiscountAmt = combinedInvoiceBatches.reduce((sum, b) => {
        const discountAmt = Number(b?.discount_amt) || ((Number(b?.gross_amt) || 0) * (Number(b?.discount_perc) || 0)) / 100;
        return sum + discountAmt;
    }, 0);
    // M10-15357: amount-weighted discount % from the group's Discount Percent / Fixed Price
    // batches only. A Scheme batch's discount_amt/discount_perc are always 0 (its discount is
    // expressed as free qty, not amount/percent), so this naturally excludes Scheme batches
    // rather than treating their missing discount as a real 0%.
    const combinedInvoicePercentDiscount = combinedInvoiceGrossAmt > 0 ? (combinedInvoiceDiscountAmt / combinedInvoiceGrossAmt) * 100 : 0;
    // M10-15357: fold each Scheme batch's free qty into an equivalent discount % across the
    // group's combined paid + free units — free qty ÷ (free qty + paid qty) × 100, the same
    // conversion the single-row engine uses, just applied to the combined totals.
    const combinedInvoiceSchemeEquivalentPercent =
        combinedInvoiceQuantity + combinedInvoiceSchemeQty > 0
            ? (combinedInvoiceSchemeQty / (combinedInvoiceQuantity + combinedInvoiceSchemeQty)) * 100
            : 0;
    // Compounded (not summed) — a group with no Scheme batches falls back to exactly the
    // amount-weighted percent discount above, and no batch's discount is ever counted through
    // both the percent-amount path and the scheme-qty path at once.
    const combinedInvoiceDiscountPercent =
        combinedInvoicePercentDiscount + ((100 - combinedInvoicePercentDiscount) * combinedInvoiceSchemeEquivalentPercent) / 100;
    // M10-15357: combined not-yet-mapped Quantity capacity across every selected batch of this
    // item — the basis for "does this modification fit within what's actually left on the
    // invoice" checks below, so a multi-row selection is capped against the group's total
    // capacity instead of just the batch that opened the panel.
    const combinedInvoiceUnmappedQty = combinedInvoiceBatches.reduce((sum, b) => sum + (Number(b?.quantity) || 0), 0);
    // M10-15357: "Quantity cannot exceed what was actually invoiced" cap, capacity-aware for a
    // bulk selection — a Scheme sibling's free qty is about to be folded fully into Quantity when
    // saved (handleSaveModifiedData's siblingCapacityTotal already does this unconditionally for
    // every sibling), so the cap must count each batch's quantity + free qty together, not just
    // its quantity field. Falls back to the exact single-row values outside a bulk selection.
    const combinedInvoiceQuantityCapacity = isBulkBatchMapping
        ? combinedInvoiceBatches.reduce((sum, b) => sum + (Number(b?.quantity) || 0) + (Number(b?.free_qty) || 0), 0)
        : combinedInvoiceQuantity;
    const combinedInvoiceUnmappedQuantityCapacity = isBulkBatchMapping
        ? combinedInvoiceBatches.reduce((sum, b) => sum + (Number(b?.unmappedQty) || 0) + (Number(b?.unmappedFreeQty) || 0), 0)
        : combinedInvoiceUnmappedQty;

    const getInvoiceDataDisplayValue = (row: any) => {
        if (!isBulkBatchMapping) return row?.invoiceData;
        switch (row?.type) {
            case "Quantity":
                return combinedInvoiceQuantity;
            case "Scheme Qty":
                return combinedInvoiceSchemeQty;
            case "Discount %":
                return combinedInvoiceDiscountPercent.toFixed(2);
            default:
                return row?.invoiceData;
        }
    };

    // Builds the line item that will actually be saved for a sibling batch: its own quantity
    // (free/scheme qty folded in — a sibling is always mapped under the group's Discount Percent/
    // Fixed Price order type, never Scheme, so it never has a separate Scheme Qty to show), at the
    // Price/PTR/MRP/Cash Discount decided for the batch being edited in the panel below.
    const buildSiblingModifiedItem = (sibling: any) => {
        const siblingBaseQuantity = Number(sibling?.unmappedQty ?? sibling?.quantity ?? 0);
        const siblingBaseFreeQty = Number(sibling?.unmappedFreeQty ?? sibling?.free_qty ?? 0);
        const siblingQuantity = siblingBaseQuantity + siblingBaseFreeQty;
        const modifiedPrice = tableData.find((item) => item.type === "Price")?.modifiedData;
        const siblingBaseKey = `${sibling?.item_code}~${sibling?.batch_no}~${selectedOrders?.[0]?.id}`;
        const siblingExistingCount = mappedData.filter((item) => item.unique_key?.startsWith(siblingBaseKey)).length;
        return {
            ...sibling,
            quantity: siblingQuantity,
            free_qty: 0,
            discount_perc: finalDiscountPerc,
            discount_type: selectedOrders?.[0]?.price_identifier === 1 ? "Discount Percent" : "Fixed Price",
            cash_discount: tableData.find((item) => item.type === "Cash Discount")?.modifiedData,
            ptr: tableData.find((item) => item.type === "PTR")?.modifiedData,
            inv_mrp: tableData.find((item) => item.type === "MRP")?.modifiedData,
            inv_rate: modifiedPrice,
            gross_amt: Number(modifiedPrice) * siblingQuantity,
            fixed_price: selectedOrders?.[0]?.price_identifier === 3 ? modifiedPrice : null,
            invoice_id: invoiceApprovalData?.invoice?.invoice_id,
            company_stockist_code: String(invoiceApprovalData?.company_stockist_code),
            counter_code: invoiceApprovalData?.counter_code,
            order_code: selectedOrders?.map((item: any) => item?.id),
            p_cash_discount_perc: Number(tableData.find((item) => item.type === "Cash Discount")?.modifiedData),
            merged: true,
            unique_key: `${siblingBaseKey}~${siblingExistingCount + 1}`,
        };
    };

    const siblingModifiedItems = siblingBatches.map(buildSiblingModifiedItem);

    const uniqueKeyColorMap: Record<string, string> = {};
    let colorIndex = 0;

    mappedData.forEach((item) => {
        if (!uniqueKeyColorMap[item.unique_key?.split("~")?.slice(0, 3)?.join("~")]) {
            uniqueKeyColorMap[item.unique_key?.split("~")?.slice(0, 3)?.join("~")] = subtleColors[colorIndex % subtleColors.length];
            colorIndex++;
        }
    });

    const outSideClickRef = useOutsideClick(() => {
        setVersioningModal(false);
    });
    const { withEventCheck } = useEventStatus();

    const inputRef = useRef<HTMLInputElement>(null);
    const ref = useOutsideClick(() => {
        setIsRemarksShown(false);
    });

    const handleToggle = () => {
        setIsExpanded((prev) => !prev);
    };

    const fetchInvoiceDetails = async () => {
        setIsLoading(true);
        try {
            const response = await getInvoiceDetails(stockist_invoice_attachment_id as string, false, CompanyCode);
            dispatch(invoiceActions.setInvoiceApprovalData(response?.data?.data));
            setOrderedData(
                response?.data?.data?.invoice?.invoiceProducts?.flatMap(
                    (item: any) =>
                        item?.orderProductMappings?.map((mapping: any) => ({
                            ...mapping,
                            item_description: item?.item_description,
                            item_code: item?.item_code,
                            entered_item_code: item?.item_code,
                            inv_mrp: item?.inv_mrp,
                            ptr: item?.ptr,
                            price_identifier: item?.discount_type,
                            batch_no: item?.batch_no,
                        })) || []
                )
            );
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCheckboxChange = (e: any, item: any, index: number) => {
        const rowId = getRowId(item, index);

        setCheckedRows((prev) => {
            const newSet = new Set(prev);
            if (e.target.checked) {
                newSet.add(rowId);
            } else {
                newSet.delete(rowId);
            }
            return newSet;
        });

        if (e.target.checked) {
            setSelectedProducts((prev) => {
                const existing = prev.find((p) => p.unique_key === item.unique_key);
                if (existing) {
                    return prev.map((p) =>
                        p.unique_key === item.unique_key
                            ? {
                                ...p,
                                quantity: p.quantity + item.quantity,
                                free_qty: p.free_qty + item.free_qty,
                                unmappedQty: p.unmappedQty + item.quantity,
                                unmappedFreeQty: p.unmappedFreeQty + item.free_qty,
                            }
                            : p
                    );
                } else {
                    return [...prev, { ...item }];
                }
            });
        } else {
            // Unchecking this row — need to subtract this item's quantities
            setSelectedProducts((prev) => {
                const updated = prev.map((p) => {
                    if (p.unique_key === item.unique_key) {
                        return {
                            ...p,
                            quantity: p.quantity - item.quantity,
                            free_qty: p.free_qty - item.free_qty,
                            unmappedQty: p.unmappedQty - item.quantity,
                            unmappedFreeQty: p.unmappedFreeQty - item.free_qty,
                        };
                    }
                    return p;
                });

                // If quantities zeroed, remove the item
                return updated.filter((p) => p.quantity > 0 || p.free_qty > 0);
            });
        }
    };

    const calculateTotalCashDiscount = () => {
        let total = 0;
        mappedData?.forEach((item: any) => {
            const { gross_amt, discount_perc, p_cash_discount_perc } = item;
            const afterItemDiscount = Number(gross_amt) - (Number(gross_amt) * Number(discount_perc)) / 100;
            const cashDiscountAmount = (afterItemDiscount * Number(p_cash_discount_perc)) / 100;
            total += cashDiscountAmount;
        });
        return total;
    };

    const calculateSubtotal = () => {
        let total = 0;
        mappedData?.forEach((item: any) => {
            const { gross_amt, discount_perc } = item;
            const cashDiscountAmount = (gross_amt * Number(discount_perc)) / 100;
            total += gross_amt - cashDiscountAmount;
        });
        return total;
    };

    const calculateTotalGst = () => {
        let total = 0;
        mappedData?.forEach((item: any) => {
            const { gross_amt, p_gst_perc, discount_perc, p_cash_discount_perc } = item;
            // Calculate GST on amount after discount
            let amountAfterItemDiscount = Number(gross_amt) - (Number(gross_amt) * Number(discount_perc)) / 100;
            let amountAfterCashDiscount = amountAfterItemDiscount - (amountAfterItemDiscount * Number(p_cash_discount_perc)) / 100;

            const gstAmount = (amountAfterCashDiscount * Number(p_gst_perc)) / 100;
            total += gstAmount;
        });
        return total;
    };

    const handleViewDetails = async (item: any) => {
        setSelectedOrders([]);
        setSelectedItemDetails(null);
        // setMappedData((prev) => prev.filter((mappedItem) => mappedItem.unique_key !== item.unique_key));
        try {
            const response = await getSimilarOrders({
                item_code: item?.item_code,
                inv_mrp: item?.inv_mrp,
                counter_code: invoiceApprovalData?.counter_code,
                company_stockist_code: invoiceApprovalData?.company_stockist_code,
                temp_invoice_date: tempDate, //M10-12020
                company_code: invoiceApprovalData?.company_code,
                invoice_date: invoiceApprovalData?.invoice?.new_invoice_date,   //M10-12020
                batch_no :  item?.batch_no, //M10-14279
                stockist_invoice_attachment_id: invoiceApprovalData?.stockist_invoice_attachment_id,
            });

            toast({
                title: "Data Fetched Successfully",
                description: "Data has been fetched successfully.",
            });

            setSimilarOrders(response?.data?.data);
            setSelectedItemDetails(item);

            //if uniquekey+batch is not present in remainingQuantity, then add it //M10-14279
            const hasBatchInSimilarOrders = response?.data?.data?.some((orderItem: any) => orderItem?.batch_number); //M10-14279
            const incomingKey = hasBatchInSimilarOrders
                ? item?.unique_key?.split("~").slice(0, 2).join("~")
                : item?.unique_key?.split("~")[0]; //M10-14279

            setRemainingQuantity((prev) => {
                const existingIndex = prev.findIndex((product) => product?.unique_key === incomingKey);
                //M10-13888
                const freshOrders = response?.data?.data?.map((orderItem: any) => ({
                    order_code: orderItem?.order?.order_code,
                    remaining_quantity: orderItem?.remaining_quantity ?? 0,
                    remaining_free_quantity: orderItem?.remaining_free_quantity ?? 0,
                })) ?? [];

                if (existingIndex !== -1) {
                    const prevOrders = prev[existingIndex]?.orders ?? [];
                    // Reconcile with latest API response: keep decremented entries for orders
                    // still present; drop stale orders that are no longer returned; add new ones.
                    const mergedOrders = freshOrders.map((fresh: any) => {
                        const carry = prevOrders.find((o: any) => o?.order_code === fresh.order_code);
                        return carry ? carry : fresh;
                    });

                    const updated = [...prev];
                    updated[existingIndex] = {
                        ...updated[existingIndex],
                        orders: mergedOrders,
                        remaining_invoice_quantity: item?.quantity,
                        remaining_invoice_free_quantity: item?.free_qty,
                    };
                    return updated;
                } else {
                    return [
                        ...prev,
                        {
                            unique_key: incomingKey,
                            orders: freshOrders,    //M10-13888
                            remaining_invoice_quantity: item?.quantity,
                            remaining_invoice_free_quantity: item?.free_qty,
                        },
                    ];
                }
            });

            requestAnimationFrame(() => {
                setTimeout(() => {
                    if (inputRef.current) {
                        // Store the original styles
                        const originalBorder = inputRef.current.style.border || "1px solid #d1d5db"; // Default Tailwind gray-300
                        const originalBoxShadow = inputRef.current.style.boxShadow || "none";

                        // Focus and apply new styles
                        inputRef.current.focus();
                        inputRef.current.style.boxShadow = "0 0 0 2px #8051FF";

                        // Add blur event listener to reset styles
                        const resetStyles = () => {
                            if (inputRef.current) {
                                inputRef.current.style.border = originalBorder;
                                inputRef.current.style.boxShadow = originalBoxShadow;
                            }
                        };

                        // Remove any existing listener to prevent duplicates
                        inputRef.current.removeEventListener("blur", resetStyles);
                        // Add the blur listener
                        inputRef.current.addEventListener("blur", resetStyles, { once: true });
                    }
                }, 100);
            });
            setShowOrderDropdown(true);
        } catch (error: any) {
            console.log(error);
            setSimilarOrders([]); //M10-12020
            toast({
                title: error?.response?.data?.error,
                description: error?.response?.data?.message,
                variant: "destructive",
            });
        }
    };

    useEffect(() => {
        if (!selectedItemDetails) return;
        let modifiedQuantity;
        let modifiedFreeQty;

        let remainingQty;
        let OrdRemainingFreeQty;
        let OrdDiscountType;
        let OrdQuantity;

        let discountInv;
        let discountOrd;
        let InvQty;
        let InvFreeQty;
        let fixed_price;
        let InvDiscPerc;
        let InvDiscAmt;
        let InvDiscountType;

        let selectedItemUniqueKey = (similarOrders?.some((o: any) => o?.batch_number) ? selectedItemDetails?.unique_key?.split("~").slice(0, 2).join("~") : selectedItemDetails?.unique_key?.split("~")[0]); //M10-14279

        let modifiedDiscountPercent;
        let cashDiscountInvoice = Number(selectedItemDetails?.p_cash_discount_perc) || 0;
        let cashDiscountOrder = Number(selectedOrders?.[0]?.order?.order_cash_discount_perc) || 0;

        remainingQty = remainingQuantity
            ?.find((item) => item?.unique_key === selectedItemUniqueKey)
            ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity;

        OrdRemainingFreeQty = remainingQuantity
            ?.find((item) => item?.unique_key === selectedItemUniqueKey)
            ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_free_quantity;

        InvQty = selectedItemDetails?.quantity;
        InvFreeQty = selectedItemDetails?.free_qty;
        InvDiscPerc = (selectedItemDetails?.discount_amt / selectedItemDetails?.gross_amt) * 100;
        InvDiscAmt = selectedItemDetails?.discount_amt;
        InvDiscountType = selectedItemDetails?.discount_type;

        OrdDiscountType = selectedOrders?.[0]?.price_identifier;
        OrdQuantity = selectedOrders?.[0]?.quantity;

        discountInv = selectedItemDetails?.gross_amt > 0 ? ((selectedItemDetails.discount_amt / selectedItemDetails.gross_amt) * 100).toFixed(2) : "0.00"; console.log(selectedItemDetails?.discount_amt, selectedItemDetails?.gross_amt)

        //When invoice discount type is Discount Percent and order is of scheme then allow equivalent free qty of discount of invoiced quantity
        if ((InvDiscountType == "Discount Percent" || InvDiscountType == "Discount Amount") && InvDiscPerc !== 0 && OrdDiscountType === 2) {
            const allowedFreeQtyRaw = InvQty * (InvDiscPerc / 100);
            modifiedFreeQty = Math.round(allowedFreeQtyRaw);
            modifiedQuantity = InvQty - modifiedFreeQty > remainingQty ? remainingQty : InvQty - modifiedFreeQty;
        }
        //When invoice discount type is Scheme and order is of Discount percent then allow equivalent free qty of discount of ordered quantity
        else if (InvDiscountType == "Scheme" && OrdDiscountType === 1) {
            // const allowedFreeQtyRaw = Math.round(OrdQuantity * (selectedOrders?.[0]?.discount_on_ptr / 100)); //vijay changes
            modifiedFreeQty = OrdRemainingFreeQty;
            modifiedQuantity = InvQty < remainingQty ? InvQty : remainingQty;
            // modifiedQuantity = InvQty + allowedFreeQtyRaw; //vijay changes
            // modifiedFreeQty = Math.round(allowedFreeQtyRaw);
            // modifiedQuantity = OrdQuantity - modifiedFreeQty;
        }
        //When invoice discount type is Fixed price and order is of Fixed Price
        else if (
            (InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent" || InvDiscountType == "Scheme") &&
            InvDiscPerc == 0 &&
            InvDiscAmt == 0 &&
            InvFreeQty == 0 &&
            OrdDiscountType === 3
        ) {
            const calculatedQuantity = (remainingQty * selectedOrders?.[0]?.fixed_price) / selectedItemDetails?.inv_rate;
            modifiedQuantity = Math.round(Math.min(selectedItemDetails?.quantity, calculatedQuantity));
            // modifiedFreeQty = remainingQty - modifiedQuantity;
        }
        //When invoice discount type is Fixed price and order is of Scheme then allow equivalent free qty of discount of invoiced quantity
        else if (
            (InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent") &&
            InvDiscPerc == 0 &&
            InvDiscAmt == 0 &&
            OrdDiscountType === 2
        ) {
            const absDiffInPtrAndPrice = Math.abs(selectedOrders?.[0]?.ptr - selectedItemDetails?.inv_rate);
            const allowedFreeQtyRaw = (InvQty * ((absDiffInPtrAndPrice / selectedOrders?.[0]?.ptr) * 100)) / 100;
            modifiedFreeQty = Math.round(allowedFreeQtyRaw);
            modifiedQuantity = Math.min(InvQty - modifiedFreeQty, remainingQty);
        }
        //When invoice discount type is Scheme  and order is of Fixed price then allow equivalent free qty of discount of ordered quantity
        else if (InvDiscountType == "Scheme" && OrdDiscountType === 3) {
            const absDiffInPtrAndPrice = Math.abs(selectedOrders?.[0]?.fixed_price - selectedItemDetails?.ptr);
            const allowedFreeQtyRaw = (OrdQuantity * ((absDiffInPtrAndPrice / selectedItemDetails?.ptr) * 100)) / 100;
            modifiedFreeQty = Math.round(allowedFreeQtyRaw);
            modifiedQuantity = OrdQuantity - modifiedFreeQty;
        }
        //When invoice discount type is Fixed price and order is Discount on PTR
        else if (
            (InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent") &&
            InvDiscPerc == 0 &&
            InvDiscAmt == 0 &&
            OrdDiscountType === 1
        ) {
            // const invoiceAmount = InvQty * InvRate;
            // const orderAmount = ((OrdQuantity * selectedOrders?.[0]?.ptr)) - ((OrdQuantity * selectedOrders?.[0]?.ptr) * (selectedOrders?.[0]?.discount_on_ptr / 100));
            //if difference of invoice amount and order amount is less than or equal to 1 then allow the quantity of order
            // if (Math.abs(invoiceAmount - orderAmount) <= 1) {
            modifiedQuantity = InvQty < remainingQty ? InvQty : remainingQty;
            modifiedFreeQty = InvFreeQty < OrdRemainingFreeQty ? InvFreeQty : OrdRemainingFreeQty;
            // }
        }
        //When invoice discount type is Discount on PTR and order is of Fixed price
        else if ((InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent") && InvDiscPerc !== 0 && OrdDiscountType === 3) {
            // const invoiceAmount = (InvQty * InvRate) - (InvQty * InvRate) * (InvDiscPerc / 100);
            // const orderAmount = (OrdQuantity * selectedOrders?.[0]?.fixed_price);
            //if difference of invoice amount and order amount is less than or equal to 1 then allow the quantity of order
            // if (Math.abs(invoiceAmount - orderAmount) <= 1) {
            modifiedQuantity = InvQty < remainingQty ? InvQty : remainingQty;
            modifiedFreeQty = InvFreeQty < OrdRemainingFreeQty ? InvFreeQty : OrdRemainingFreeQty;
            // }
        } else {
            if (OrdDiscountType === 2) {
                let totalDiscountPercentage1 = cashDiscountInvoice + ((100 - cashDiscountInvoice) * Number(discountInv)) / 100;
                const allowedFreeQtyRaw = InvQty * (Number(totalDiscountPercentage1) / 100);
                let calculatedFreeQty = Number(Math.round(allowedFreeQtyRaw) + InvFreeQty);
                modifiedFreeQty = Math.min(calculatedFreeQty, OrdRemainingFreeQty);
                modifiedQuantity = InvQty < remainingQty ? InvQty : remainingQty;
            } else {
                modifiedQuantity = InvQty < remainingQty ? InvQty : remainingQty;
                modifiedFreeQty = InvFreeQty < OrdRemainingFreeQty ? InvFreeQty : OrdRemainingFreeQty;
            }
        }

        discountOrd = (() => {
            switch (OrdDiscountType) {
                case 1:
                    return selectedOrders?.[0]?.discount_on_ptr;
                case 3:
                    return "Fixed Price : " + selectedOrders?.[0]?.fixed_price;
                default:
                    return "0";
            }
        })();

        fixed_price = (() => {
            switch (OrdDiscountType) {
                case 3:
                    return selectedItemDetails?.ptr;
                default:
                    return selectedItemDetails?.ptr;
            }
        })();

        //different percentage between ptr and price
        const percentageDifference = Math.abs(((selectedItemDetails?.inv_rate - selectedItemDetails?.ptr) / selectedItemDetails?.ptr) * 100).toFixed(2);
        let totalDiscountPercentage1 = cashDiscountInvoice + ((100 - cashDiscountInvoice) * Number(discountInv)) / 100;
        let totalDiscountPercentage = Number(percentageDifference) + ((100 - Number(percentageDifference)) * Number(totalDiscountPercentage1)) / 100;

        const cashDiscountModifiedData = Number(cashDiscountOrder);
        let totalDiscountcalculationWithoutScheme = Number(
            (Number(totalDiscountPercentage) - Number(cashDiscountOrder)) / (1 - Number(cashDiscountOrder) / 100)
        ).toFixed(2);

        let schemeFree = (Number(selectedItemDetails?.free_qty / (selectedItemDetails?.free_qty + InvQty)) * 100).toFixed(2);
        let totalDiscountcalculation = Number(
            Number(totalDiscountcalculationWithoutScheme) + ((100 - Number(totalDiscountcalculationWithoutScheme)) * Number(schemeFree)) / 100
        ).toFixed(2);
        //=G27/(G27+F27)

        // if (InvDiscountType == "Scheme" && OrdDiscountType == 1) {
        //   modifiedDiscountPercent = discountOrd;
        // } else {
        //   let discountVal = (100 * cashDiscountModifiedData - 100 * cashDiscountInvoice) / (cashDiscountModifiedData - 100)
        //   // modifiedDiscountPercent = OrdDiscountType === 3 ? "0" : discountInv < discountOrd ? discountInv : discountOrd;
        //   let modifiedDataPer = OrdDiscountType === 3 ? "0" : discountInv < discountOrd ? discountInv : discountOrd
        //   modifiedDiscountPercent = Number(
        //     Math.min(modifiedDataPer, Number(discountVal)).toFixed(2)
        //   );
        // }
        if (InvDiscountType == "Scheme" && OrdDiscountType == 1) {
            modifiedDiscountPercent = discountOrd;
        } else {
            modifiedDiscountPercent = Math.min(Number(totalDiscountcalculation), Number(discountOrd) || 0);
        }
        // M10-15357: when batches are bulk-mapped, the suggested Modified Discount % should also
        // fold in the sibling batches' Scheme free qty — the same combined scheme-to-discount
        // conversion already used for the "Invoice Data" column (combinedInvoiceDiscountPercent),
        // capped at the order's own discount %, instead of only this one row's own figures.
        if (isBulkBatchMapping) {
            modifiedDiscountPercent = Number(
                Math.min(Number(discountOrd) || 0, combinedInvoiceDiscountPercent).toFixed(2)
            );
        }
        let modified_price = selectedItemDetails?.ptr;
        if (OrdDiscountType == 3) {
            if (selectedOrders?.[0]?.fixed_price) {
                // =I15*(100-J15)/100
                const calculationData = Number((Number(selectedItemDetails?.inv_rate) * (100 - Number(discountInv))) / 100).toFixed(2);
                // Replaced-pack line: the order floor is its per-tablet equivalent for the invoice pack.
            modified_price = Math.max(Number(calculationData), packEquivalentPrice(selectedItemDetails, selectedOrders?.[0]) ?? selectedOrders?.[0]?.fixed_price);
            }
        }

        // modifiedFixedPrice = Number(selectedItemDetails?.inv_rate) > Number(fixed_price) ? selectedItemDetails?.inv_rate : fixed_price;

        const newTableData = [
            {
                type: "Quantity",
                invoiceData: selectedItemDetails?.quantity,
                orderedData: String(selectedOrders?.reduce((acc: number, curr: any) => acc + Number(curr?.quantity), 0)),
                // modifiedData: selectedItemDetails?.quantity,

                modifiedData: modifiedQuantity,
                remaining_quantity: remainingQty,
                allowed_quantity: modifiedQuantity,
            },
            {
                type: "Discount %",
                invoiceData: discountInv,
                orderedData: discountOrd,
                modifiedData: modifiedDiscountPercent,
            },
            {
                type: "Cash Discount",
                invoiceData: cashDiscountInvoice,
                orderedData: cashDiscountOrder,
                // modifiedData: selectedItemDetails?.p_cash_discount_perc || 0,
                modifiedData: cashDiscountModifiedData,
                // modifiedData: data?.item_discount,
            },
            {
                type: "Scheme Qty",
                invoiceData: selectedItemDetails?.free_qty,
                orderedData: OrdDiscountType === 2 ? selectedOrders?.[0]?.free_quantity : "0",
                // modifiedData: selectedItemDetails?.free_qty,
                modifiedData: modifiedFreeQty,
                allowed_free_qty: modifiedFreeQty,
            },
            {
                type: "PTR",
                invoiceData: selectedItemDetails?.ptr,
                // orderedData: selectedOrders?.[0]?.ptr,
                orderedData: selectedItemDetails?.ptr,
                modifiedData: selectedItemDetails?.ptr,
            },
            {
                type: "MRP",
                invoiceData: selectedItemDetails?.inv_mrp,
                orderedData: selectedItemDetails?.inv_mrp,
                // orderedData: selectedOrders?.[0]?.mrp,
                modifiedData: selectedItemDetails?.inv_mrp,
            },
            {
                type: "Price",
                invoiceData: selectedItemDetails?.inv_rate,
                orderedData: fixed_price,
                modifiedData: String(modified_price),
            },
            {
                type: "Calculated Value",
                invoiceData: Number(
                    selectedItemDetails?.quantity * selectedItemDetails?.inv_rate * (1 - Number(discountInv) / 100) * (1 - cashDiscountInvoice / 100)
                ).toFixed(2),
                orderedData: "-",
                modifiedData: Number(
                    modifiedQuantity * modified_price * (1 - Number(modifiedDiscountPercent) / 100) * (1 - cashDiscountModifiedData / 100)
                ).toFixed(2),
            },
        ];

        setTableData(newTableData);
    }, [selectedOrders]);

    //On change of modified data
    const handleModifyData = (index: number, value: string) => {
        const updatedTableData = [...tableData];

        const currentType = tableData[index].type;
        const newValue = Number(value);

        //Scheme Validation
        if (currentType === "Scheme Qty") {
            const orderedFreeQuantity = Number(
                remainingQuantity
                    ?.find((item) => item?.unique_key === (similarOrders?.some((o: any) => o?.batch_number) ? selectedItemDetails?.unique_key?.split("~").slice(0, 2).join("~") : selectedItemDetails?.unique_key?.split("~")[0]))
                    ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_free_quantity
            );
            const remainingInvoicedFreeQuantity = Number(
                remainingQuantity?.find((item) => item?.unique_key === (similarOrders?.some((o: any) => o?.batch_number) ? selectedItemDetails?.unique_key?.split("~").slice(0, 2).join("~") : selectedItemDetails?.unique_key?.split("~")[0]))?.remaining_invoice_free_quantity
            );

            if (selectedItemDetails?.discount_type == "Scheme" && selectedOrders?.[0]?.price_identifier !== 2) {
                if (remainingInvoicedFreeQuantity < orderedFreeQuantity && newValue > remainingInvoicedFreeQuantity) {
                    toast({
                        description: `Modified Scheme Qty cannot be greater than remaining invoice free quantity ${remainingInvoicedFreeQuantity}`,
                        variant: "destructive",
                    });
                }
            }

            if (selectedItemDetails?.discount_type == "Scheme" && selectedOrders?.[0]?.price_identifier !== 1) {
                if (orderedFreeQuantity < remainingInvoicedFreeQuantity && newValue > orderedFreeQuantity) {
                    toast({
                        description: `Modified Scheme Qty cannot be greater than remaining order free quantity ${orderedFreeQuantity}`,
                        variant: "destructive",
                    });
                }
            }

            if (isNaN(newValue) || newValue < 0) {
                toast({
                    title: "Invalid Scheme Quantity",
                    description: "Please enter a valid positive number",
                    variant: "destructive",
                });
                return;
            }
        }

        // Quantity validation
        if (currentType === "Quantity") {
            const orderedQuantity = Number(
                remainingQuantity
                    ?.find((item) => item?.unique_key === (similarOrders?.some((o: any) => o?.batch_number) ? selectedItemDetails?.unique_key?.split("~").slice(0, 2).join("~") : selectedItemDetails?.unique_key?.split("~")[0]))
                    ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity
            );

            // const remainingInvoicedQuantity = Number(
            //   remainingQuantity?.find((item) => item?.unique_key === (similarOrders?.some((o: any) => o?.batch_number) ? selectedItemDetails?.unique_key?.split("~").slice(0, 2).join("~") : selectedItemDetails?.unique_key?.split("~")[0]))?.remaining_invoice_quantity
            // );

            // M10-15357: capacity-aware combined invoice Quantity across every selected batch of
            // this item — folds a Scheme sibling's free qty into its Quantity contribution for a
            // bulk selection (falls back to the plain single-row value otherwise).
            const remainingInvoicedQuantity = combinedInvoiceUnmappedQuantityCapacity;
            const InvDiscountType = selectedItemDetails?.discount_type;
            const OrdDiscountType = selectedOrders?.[0]?.price_identifier;

            if (!(InvDiscountType === "Scheme" && OrdDiscountType !== 2) && newValue > remainingInvoicedQuantity) { //M10-14122
                toast({
                    description: `Modified quantity cannot be greater than remaining invoice quantity ${remainingInvoicedQuantity}`,
                    variant: "destructive",
                });
            }

            if (newValue > orderedQuantity) {
                toast({
                    description: `Modified quantity cannot be greater than remaining order quantity ${orderedQuantity}`,
                    variant: "destructive",
                });
            }

            if (isNaN(newValue) || newValue < 0) {
                toast({
                    title: "Invalid Quantity",
                    description: "Please enter a valid positive number",
                    variant: "destructive",
                });
                return;
            }
        }

        // Discount % validation
        if (currentType === "Discount %") {
            const orderedDiscountType = Number(tableData[index].orderedData);
            if (newValue > orderedDiscountType) {
                toast({
                    description: `Modified discount % cannot be greater than ordered discount % ${orderedDiscountType}`,
                    variant: "destructive",
                });
            }
        }

        //Price Validation
        if (currentType === "Price") {
            const orderedPrice = Number(tableData[index].orderedData);
            const InvoicePrice = Number(tableData[index].invoiceData);
            if (orderedPrice > InvoicePrice && newValue > orderedPrice) {
                toast({
                    description: `Modified Price cannot be greater than ordered price ${orderedPrice}`,
                    variant: "destructive",
                });
            }
            if (InvoicePrice > orderedPrice && newValue > InvoicePrice) {
                toast({
                    description: `Modified Price cannot be greater than ordered price ${InvoicePrice}`,
                    variant: "destructive",
                });
            }
        }

        // M10-14735: Once Discount % or Scheme Qty is modified, Modified Price must be within 5% of Invoice PTR
        if (currentType === "Discount %" || currentType === "Scheme Qty") {
            const priceRow = tableData.find((row) => row.type === "Price");
            const ptrRow = tableData.find((row) => row.type === "PTR");
            const invoicePtr = Number(ptrRow?.invoiceData);
            const modifiedPrice = Number(priceRow?.modifiedData);
            const minAcceptablePrice = invoicePtr * 0.95;

            if (newValue !== 0 && (modifiedPrice > invoicePtr || modifiedPrice < minAcceptablePrice)) {
                toast({
                    description: `Modified Price must be within 5% of Invoice PTR (${invoicePtr}), i.e. not less than ${minAcceptablePrice.toFixed(2)}, to modify ${currentType}`,
                    variant: "destructive",
                });
                return;
            }
        }

        // Update the current modified value
        updatedTableData[index].modifiedData = value;

        // Mutual reset logic
        const discountIndex = tableData.findIndex((item) => item.type === "Discount %");
        const schemeIndex = tableData.findIndex((item) => item.type === "Scheme Qty");

        if (currentType === "Discount %" && schemeIndex !== -1) {
            updatedTableData[schemeIndex].modifiedData = "0";
        }

        if (currentType === "Scheme Qty" && discountIndex !== -1) {
            updatedTableData[discountIndex].modifiedData = "0";
        }
        // 🔽 Recalculate "Calculated Value"
        const quantityRow = updatedTableData.find((item) => item.type === "Quantity");
        const priceRow = updatedTableData.find((item) => item.type === "Price");
        const discountRow = updatedTableData.find((item) => item.type === "Discount %");
        const cashDiscountRow = updatedTableData.find((item) => item.type === "Cash Discount");
        const calculatedValueRowIndex = updatedTableData.findIndex((item) => item.type === "Calculated Value");

        const modifiedQuantity = Number(quantityRow?.modifiedData || 0);
        const modifiedPrice = Number(priceRow?.modifiedData || 0);
        const modifiedDiscount = Number(discountRow?.modifiedData || 0);
        const modifiedCashDiscount = Number(cashDiscountRow?.modifiedData || 0);

        const modifiedCalculatedValue = (
            modifiedQuantity *
            modifiedPrice *
            (1 - modifiedDiscount / 100) *
            (1 - modifiedCashDiscount / 100)
        ).toFixed(2);

        if (calculatedValueRowIndex !== -1) {
            updatedTableData[calculatedValueRowIndex].modifiedData = modifiedCalculatedValue;
        }

        setTableData(updatedTableData);
    };

    // Pack change confirmed: the server moves the quantity to a pack-change order (or reprices the
    // untouched line). The line stays unmapped; the user maps it to that order via View.
    const confirmPackChange = async (packPayload: any) => {
        try {
            const response = await postInvoicePackChange({ ...packPayload, confirm: true }, company_code || CompanyCode);
            const result = response?.data?.data;
            for (const streamId of result?.stream_ids ?? []) {
                await withEventCheck(() => undefined, streamId);
            }
            setSelectedItemDetails(null);
            setTableData(DEFAULT_TABLE_DATA);
            setSelectedOrders([]);
            setSimilarOrders([]);
            setOrderToSelect([]);
            toast({
                title: "Order updated for the supplied pack",
                description: `Click View on this line and map it to order ${result?.order_code}.`,
            });
        } catch (error: any) {
            toast({
                description: `Pack change failed: ${error?.response?.data?.message || error?.message || "unknown error"}`,
                variant: "destructive",
            });
        }
    };

    const saveModifiedData = async (selectedOrders: any[]) => {
        setTcs(Number(invoiceApprovalData?.invoice?.tcs_perc));
        if (!selectedItemDetails) return;

        if (selectedOrders?.length == 0) {
            toast({ description: "Choose an order before submission", variant: "destructive" });
            return;
        }

        const getRemainingItem = () => {
            return remainingQuantity?.find((item) => item?.unique_key === (similarOrders?.some((o: any) => o?.batch_number) ? selectedItemDetails?.unique_key?.split("~").slice(0, 2).join("~") : selectedItemDetails?.unique_key?.split("~")[0]));
        };

        const showError = (message: string) => {
            toast({ description: message, variant: "destructive" });
            return true;
        };


        // Pack (per_strip) change on a fixed-price order with a replace: the server plans a pack-change
        // order (create / top up / reprice in place). The line is never mapped to the old-pack row.
        const packOrder = selectedOrders?.[0];
        const packQuantity = Number(tableData.find((row) => row.type === "Quantity")?.modifiedData) || 0;
        if (
            packOrder?.price_identifier === 3 &&
            packQuantity > 0 &&
            (packOrder?.per_strip == null ||
                selectedItemDetails?.per_strip == null ||
                Number(packOrder?.per_strip) !== Number(selectedItemDetails?.per_strip))
        ) {
            const packPayload = {
                invoice_id: invoiceApprovalData?.invoice?.invoice_id,
                stockist_invoice_attachment_id: Number(stockist_invoice_attachment_id),
                invoice_product_id: selectedItemDetails?.invoice_product_id,
                item_code: packOrder?.replaced_products?.length > 0 ? packOrder?.item_code : selectedItemDetails?.item_code,
                entered_item_code: selectedItemDetails?.item_code,
                order_code: packOrder?.id,
                quantity: packQuantity,
                inv_rate: Number(tableData.find((row) => row.type === "Price")?.modifiedData),
                inv_mrp: Number(tableData.find((row) => row.type === "MRP")?.modifiedData),
                is_replaced: Boolean(selectedItemDetails?.is_replaced || packOrder?.is_replaced || packOrder?.replaced_products?.length),
            };
            let plan: any;
            try {
                plan = (await postInvoicePackChange({ ...packPayload, confirm: false }, company_code || CompanyCode))?.data?.data?.plan;
            } catch (error: any) {
                showError(`Pack check failed: ${error?.response?.data?.message || error?.message || "unknown error"}`);
                return;
            }
            if (plan?.action === "use_existing") {
                showError(plan.message);
                return;
            }
            if (plan && plan.action !== "none") {
                setPackChangeMessage(plan.message);
                perStripContinueRef.current = (confirmed) => {
                    if (confirmed) confirmPackChange(packPayload);
                };
                setOpenPerStripModal(true);
                return;
            }
        }

        const hasInvalidQuantity = tableData.some((item) => {
            const modifiedValue = Number(item?.modifiedData);
            // M10-15357: use the combined (Quantity/Scheme Qty/Discount %) invoice value across
            // every selected batch of this item, not just this one batch's own.
            const invoiceValue = Number(getInvoiceDataDisplayValue(item));
            const orderedValue = Number(item?.orderedData);
            const remainingItem = getRemainingItem();
            const remainingOrderQuantity = remainingItem?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity;
            // M10-15357: capacity-aware — folds a Scheme sibling's free qty into its Quantity
            // contribution for a bulk selection (falls back to the plain single-row value otherwise).
            const remainingInvoiceQuantity = combinedInvoiceUnmappedQuantityCapacity;
            const remainingFreeQuantity = remainingItem?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)
                ?.remaining_free_quantity;
            const InvDiscountType = selectedItemDetails?.discount_type;
            const OrdDiscountType = selectedOrders?.[0]?.price_identifier;

            // Quantity validations
            if (item?.type === "Quantity") {
                if (modifiedValue > remainingOrderQuantity) {
                    return showError("Modified quantity cannot be greater than remaining order quantity");
                }
                if (!(InvDiscountType === "Scheme" && OrdDiscountType !== 2) && modifiedValue > remainingInvoiceQuantity) { //M10-14122
                    return showError(`Modified quantity cannot be greater than remaining invoice quantity ${remainingInvoiceQuantity}`);
                }
                // M10-15357: capacity-aware cap (combinedInvoiceQuantityCapacity), not the plain
                // combinedInvoiceQuantity used for display — a Scheme sibling's free qty is about
                // to be folded fully into Quantity when saved, so it must count toward this cap.
                if (!(InvDiscountType === "Scheme" && OrdDiscountType !== 2) && modifiedValue > combinedInvoiceQuantityCapacity) { //M10-14122
                    return showError("Quantity cannot be greater than invoiced quantity");
                }
                // if (modifiedValue > allowedQty) {
                //   return showError("Quantity cannot be greater than allowed quantity");
                // }
            }

            // Scheme Qty validations
            if (item?.type === "Scheme Qty") {
                if (
                    !(InvDiscountType === "Scheme" && OrdDiscountType === 3) &&
                    !(InvDiscountType === "Scheme" && OrdDiscountType === 1) &&
                    modifiedValue > remainingFreeQuantity
                ) {
                    return showError("Modified free quantity cannot be greater than remaining free quantity");
                }

                if (
                    !(InvDiscountType === "Scheme" && OrdDiscountType === 3) &&
                    !(InvDiscountType === "Scheme" && OrdDiscountType === 1) &&
                    modifiedValue > orderedValue
                ) {
                    return showError("Scheme Qty cannot be greater than ordered quantity");
                }
            }

            // Price validations
            if (item?.type === "Price") {
                if (invoiceValue > orderedValue && modifiedValue > invoiceValue) {
                    return showError("Price cannot be greater than invoiced price");
                }
                if (orderedValue > invoiceValue && modifiedValue > orderedValue) {
                    return showError("Price cannot be greater than order price");
                }
                if (OrdDiscountType !== 3 && modifiedValue < orderedValue) {
                    return showError(`Modified Price (${modifiedValue}) cannot be less than the order price (${orderedValue}) for a non-fixed-price order`);
                }

                // M10-14735: Discount % / Scheme Qty modification requires Modified Price to be within 5% of Invoice PTR
                const discountRow = tableData.find((t) => t.type === "Discount %");
                const schemeRow = tableData.find((t) => t.type === "Scheme Qty");
                const ptrRow = tableData.find((t) => t.type === "PTR");
                const discountModified = Number(discountRow?.modifiedData) || 0;
                const schemeModified = Number(schemeRow?.modifiedData) || 0;
                const invoicePtr = Number(ptrRow?.invoiceData);
                const minAcceptablePrice = invoicePtr * 0.95;
                if ((discountModified !== 0 || schemeModified !== 0) && (modifiedValue > invoicePtr || modifiedValue < minAcceptablePrice)) {
                    return showError(`Modified Price must be within 5% of Invoice PTR (${invoicePtr}), i.e. not less than ${minAcceptablePrice.toFixed(2)}, when Discount % or Scheme Qty is modified`);
                }
            }

            // Discount validations
            if (item?.type === "Discount %" && modifiedValue > orderedValue) {
                return showError("Discount cannot be greater than ordered discount");
            }

            if (item?.type === "Cash Discount") {
                if (orderedValue < modifiedValue) {
                    return showError("Cash Discount cannot be greater");
                }
            }

            return false;
        });

        if (hasInvalidQuantity) {
            return;
        }

        let modifiedDiscountType = "";
        if (selectedOrders?.[0]?.price_identifier === 1) {
            modifiedDiscountType = "Discount Percent"
        } else if (selectedOrders?.[0]?.price_identifier === 2) {
            modifiedDiscountType = "Scheme"
        } else {
            modifiedDiscountType = "Fixed Price";
        }

        const orderCodes = selectedOrders?.map((item: any) => item?.id);

        const baseKey = `${selectedItemDetails?.item_code}~${selectedItemDetails?.batch_no}~${selectedOrders?.[0]?.id}`;
        const existingCount = mappedData.filter((item) => item.unique_key?.startsWith(baseKey)).length;
        const finalUniqueKey = `${baseKey}~${existingCount + 1}`;
        let modifiedQuantity = Number(tableData.find((item) => item.type === "Quantity")?.modifiedData) || 0;
        const modifiedFreeQty = Number(tableData.find((item) => item.type === "Scheme Qty")?.modifiedData) || 0;

        // siblingBatches/isBulkBatchMapping/finalDiscountPerc/siblingModifiedItems are computed at
        // component scope (near getRowId above) so the sibling preview panels below render off the
        // same values this save uses — see that block for the full rationale.
        // M10-15357: when batches are bulk-mapped, Modified Quantity in the panel is the GROUP's
        // combined total (matching the combined "Invoice Data → Quantity" shown above it), not just
        // this row's own share — the sibling batch(es) always ride along at their own full historical
        // quantity on top. So validate the entered total directly against the group's capacity (no
        // extra addition — that was double-counting the siblings), then give this row only its own
        // share of that total (entered total minus what the siblings already account for) so what
        // actually gets saved across the separate line items sums to exactly what was entered.
        if (isBulkBatchMapping) {
            const siblingCapacityTotal = siblingBatches.reduce(
                (sum, p) => sum + Number(p?.unmappedQty ?? p?.quantity ?? 0) + Number(p?.unmappedFreeQty ?? p?.free_qty ?? 0),
                0
            );
            const enteredGroupTotal = modifiedQuantity + modifiedFreeQty;
            if (enteredGroupTotal < siblingCapacityTotal) {
                showError(
                    `Modified Quantity + Scheme Qty (${enteredGroupTotal}) must be at least the sibling batches' own quantity (${siblingCapacityTotal}) already included in the combined total`
                );
                return;
            }
            const remainingItem = getRemainingItem();
            const remainingOrderQtyForGroup = remainingItem?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)
                ?.remaining_quantity;
            if (remainingOrderQtyForGroup != null && enteredGroupTotal > remainingOrderQtyForGroup) {
                showError(
                    `Combined quantity across selected batches (${enteredGroupTotal}) cannot be greater than remaining order quantity (${remainingOrderQtyForGroup})`
                );
                return;
            }
            modifiedQuantity = modifiedQuantity - siblingCapacityTotal;
        }

        let grossAmount = Number(tableData.find((item) => item.type === "Price")?.modifiedData) * Number(modifiedQuantity);

        const modifiedItem = {
            ...selectedItemDetails,
            // Original entered item code travels alongside item_code for the per-strip check.
            entered_item_code: selectedItemDetails?.entered_item_code ?? selectedItemDetails?.item_code,
            quantity: modifiedQuantity,
            free_qty: modifiedFreeQty,
            discount_perc: finalDiscountPerc,
            discount_type: modifiedDiscountType === 'Fixed Price' ? selectedItemDetails?.discount_type : modifiedDiscountType,
            cash_discount: tableData.find((item) => item.type === "Cash Discount")?.modifiedData,
            ptr: tableData.find((item) => item.type === "PTR")?.modifiedData,
            inv_mrp: tableData.find((item) => item.type === "MRP")?.modifiedData,
            inv_rate: tableData.find((item) => item.type === "Price")?.modifiedData,
            gross_amt: grossAmount,
            fixed_price: modifiedDiscountType === 'Fixed Price' ? tableData.find((item) => item.type === "Price")?.modifiedData : null,
            invoice_id: invoiceApprovalData?.invoice?.invoice_id,
            company_stockist_code: String(invoiceApprovalData?.company_stockist_code),
            counter_code: invoiceApprovalData?.counter_code,
            order_code: orderCodes,
            p_cash_discount_perc: Number(tableData.find((item) => item.type === "Cash Discount")?.modifiedData),
            unique_key: finalUniqueKey,
        };

        setProductsData((prev) =>
            prev.map((item) => {
                const modifiedBaseKey = modifiedItem.unique_key?.split("~").slice(0, 2).join("~");

                const matchedSibling = siblingModifiedItems.find(
                    (sibling) => item.unique_key === sibling.unique_key?.split("~").slice(0, 2).join("~")
                );
                if (matchedSibling) {
                    return { ...item, mapped: true, merged: true, unmappedQty: 0, unmappedFreeQty: 0, invoice_status: "fully_invoiced" };
                }

                if (item.unique_key !== modifiedBaseKey) return item;
                const InvDiscountType = selectedItemDetails?.discount_type;
                const OrdDiscountType = selectedOrders?.[0]?.price_identifier;

                let status = "not_invoiced";
                let unmappedQty;
                let unmappedFreeQty;

                //Inv -> Discount on PTR && Ord -> Scheme
                if (
                    (InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent") &&
                    selectedItemDetails?.discount_amt !== 0 &&
                    OrdDiscountType == 2
                ) {
                    if (modifiedQuantity + modifiedFreeQty === selectedItemDetails?.unmappedQty) {
                        status = "fully_invoiced";
                        unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity - modifiedFreeQty;
                        unmappedFreeQty = selectedItemDetails?.unmappedFreeQty;
                    } else if (modifiedQuantity + modifiedFreeQty < selectedItemDetails?.unmappedQty) {
                        status = "partially_invoiced";
                        unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity - modifiedFreeQty;
                        unmappedFreeQty = selectedItemDetails?.unmappedFreeQty;
                    }
                }

                //Inv -> Fixed Price && Ord -> Scheme
                else if (
                    (InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent") &&
                    selectedItemDetails?.discount_amt === 0 &&
                    OrdDiscountType === 2
                ) {
                    if (modifiedQuantity + modifiedFreeQty === selectedItemDetails?.unmappedQty) {
                        status = "fully_invoiced";
                        unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity - modifiedFreeQty;
                        unmappedFreeQty = selectedItemDetails?.unmappedFreeQty;
                    } else if (modifiedQuantity + modifiedFreeQty < selectedItemDetails?.unmappedQty) {
                        status = "partially_invoiced";
                        unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity - modifiedFreeQty;
                        unmappedFreeQty = selectedItemDetails?.unmappedFreeQty;
                    }
                }

                //Inv -> scheme Ord -> Discount on PTR
                else if (InvDiscountType == "Scheme" && OrdDiscountType === 1) {
                    if (modifiedQuantity + modifiedFreeQty === selectedItemDetails?.unmappedQty + selectedItemDetails?.unmappedFreeQty) {
                        status = "fully_invoiced";
                        unmappedQty = 0;
                        unmappedFreeQty = 0;
                    } else if (modifiedQuantity + modifiedFreeQty < selectedItemDetails?.unmappedQty + selectedItemDetails?.unmappedFreeQty) {
                        status = "partially_invoiced";
                        unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
                        unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
                    }
                }

                //Inv -> Fixed Price && Ord -> Discount on PTR
                else if (
                    (InvDiscountType == "Discount Amount" || InvDiscountType == "Discount Percent") &&
                    selectedItemDetails?.discount_amt == 0 &&
                    OrdDiscountType === 1
                ) {
                    if (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) {
                        status = "partially_invoiced";
                        unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
                        unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
                    } else if (modifiedQuantity === selectedItemDetails?.unmappedQty || modifiedFreeQty === selectedItemDetails?.unmappedFreeQty) {
                        status = "fully_invoiced";
                        unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
                        unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
                    }
                }

                //Inv -> Scheme && Ord -> Fixed Price
                else if (InvDiscountType == "Scheme" && OrdDiscountType === 3) {
                    if (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) {
                        status = "partially_invoiced";
                        unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
                        unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
                    } else if (modifiedQuantity === selectedItemDetails?.unmappedQty || modifiedFreeQty === selectedItemDetails?.unmappedFreeQty) {
                        status = "fully_invoiced";
                        unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
                        unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
                    }
                }

                //Inv -> Discount on PTR && Ord -> Fixed Price
                else if (
                    InvDiscountType == "Discount Amount" ||
                    (InvDiscountType == "Discount Percent" && selectedItemDetails?.discount_amt !== 0 && OrdDiscountType === 3)
                ) {
                    if (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) {
                        status = "partially_invoiced";
                        unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
                        unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
                    } else if (modifiedQuantity === selectedItemDetails?.unmappedQty || modifiedFreeQty === selectedItemDetails?.unmappedFreeQty) {
                        status = "fully_invoiced";
                        unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
                        unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
                    }
                } else {
                    if (modifiedQuantity < selectedItemDetails?.unmappedQty || modifiedFreeQty < selectedItemDetails?.unmappedFreeQty) {
                        status = "partially_invoiced";
                        unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
                        unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
                    } else if (modifiedQuantity === selectedItemDetails?.unmappedQty || modifiedFreeQty === selectedItemDetails?.unmappedFreeQty) {
                        status = "fully_invoiced";
                        unmappedQty = selectedItemDetails?.unmappedQty - modifiedQuantity;
                        unmappedFreeQty = selectedItemDetails?.unmappedFreeQty - modifiedFreeQty;
                    }
                }

                return {
                    ...item,
                    mapped: true,
                    merged: selectedProducts?.length > 0,
                    unmappedQty,
                    unmappedFreeQty,
                    invoice_status: status,
                };
            })
        );

        setMappedData((prev) => [...prev, modifiedItem, ...siblingModifiedItems]);
        setIsMapped(false)  //M10-11117

        // Sibling batches consume the same order's remaining pool — fold their quantities into the
        // same decrement so the group's combined usage is reflected in one update.
        const siblingQtyTotal = siblingModifiedItems.reduce((sum, s) => sum + Number(s.quantity || 0), 0);
        const siblingFreeQtyTotal = siblingModifiedItems.reduce((sum, s) => sum + Number(s.free_qty || 0), 0);
        const totalMappedQuantity = modifiedItem.quantity + siblingQtyTotal;
        const totalMappedFreeQty = modifiedItem.free_qty + siblingFreeQtyTotal;

        // Decrease the remaining quantity of the selected order
        setRemainingQuantity((prev) =>
            prev.map((item) => {
                const modifiedBaseKey = modifiedItem.unique_key?.split("~").slice(0, 2).join("~");
                const isMatchingItem = item.unique_key === modifiedBaseKey?.split("~")[0];

                if (!isMatchingItem) return item;

                return {
                    ...item,
                    orders: item.orders.map((order: any) => {
                        if (order.order_code !== selectedOrders?.[0]?.id) return order;

                        const isScheme =
                            selectedItemDetails?.discount_type === "Scheme" &&
                            (selectedOrders?.[0]?.price_identifier === 1 || selectedOrders?.[0]?.price_identifier === 3);

                        return {
                            ...order,
                            remaining_quantity: isScheme
                                ? order.remaining_quantity - totalMappedQuantity - totalMappedFreeQty
                                : order.remaining_quantity - totalMappedQuantity,
                            remaining_free_quantity: isScheme ? order.remaining_free_quantity : order.remaining_free_quantity - totalMappedFreeQty,
                        };
                    }),
                    remaining_invoice_quantity: item?.remaining_invoice_quantity - totalMappedQuantity,
                    remaining_invoice_free_quantity: item?.remaining_invoice_free_quantity - totalMappedFreeQty,
                };
            })
        );

        setSelectedProducts([]);
        setCheckedRows(new Set());
        setSelectedItemDetails(null);
        setTableData(DEFAULT_TABLE_DATA);
        setSelectedOrders([]);
        setSimilarOrders([]);
        setOrderToSelect([]);

        toast({
            title: "Data Mapped Successfully",
            description: "Item has been added to mapped data.",
        });
    };

    const handleReset = (invoiceApprovalData: any) => {
        setSelectedOrders([]);
        const data = invoiceApprovalData?.invoice?.invoiceProducts?.map((item: any) => ({
            ...item,
            unmappedQty: item?.quantity,
            unmappedFreeQty: item?.free_qty,
            unique_key: `${item?.item_code}~${item?.batch_no}`,
        }));
        setProductsData(data);
        setRemainingQuantity([]);
        setMappedData([]);
        setTableData(DEFAULT_TABLE_DATA);
        setSelectedItemDetails(null);
        setIsMapped(true) //M10-11117
        toast({
            title: "Data Reset Successfully",
            description: "All data has been reset.",
        });
    };

    const isRequestInProgress = useRef(false);

    const approveInvoice = async () => {
        if (isRequestInProgress.current) return;
        isRequestInProgress.current = true;

        await withEventCheck(() => { }, `invoice_${invoiceApprovalData?.invoice?.invoice_id}`);
        // const invoiceActionAllowedResponse = await getInvoiceActionAllowed(
        //     invoiceApprovalData?.invoice?.invoice_id,
        //     invoiceApprovalData?.stockist_invoice_attachment_id,
        //     "Approval",
        //     invoiceApprovalData?.company_code
        // );
        // if (!invoiceActionAllowedResponse?.data?.data?.allowed) {
        //     toast({ description: invoiceActionAllowedResponse?.data?.data?.message, variant: "destructive" });
        //     return;
        // }
        if (mappedData.length == 0) {
            toast({ description: "Please add products", variant: "default" });
            return;
        }

        // M10-15166: Price cannot exceed PTR by more than 5% (checked across all products)
        const priceExceedsPtrItem = mappedData.find((item: any) => isPriceExceedingPtr(item?.inv_rate, item?.ptr));
        if (priceExceedsPtrItem) {
            toast({
                description: `Price cannot exceed PTR by more than 5% for item ${priceExceedsPtrItem?.item_description || priceExceedsPtrItem?.item_code}.`,
                variant: "destructive",
            });
            isRequestInProgress.current = false;
            return;
        }

        setIsApproving(true);

        // const currentDate = new Date(invoiceApprovalData?.invoice?.new_invoice_date);
        // const currentYear = currentDate.getFullYear();
        // const financialYearStart = new Date(currentDate.getMonth() >= 3 ? currentYear : currentYear - 1, 3, 1);

        // const invoiceAccYear = financialYearStart.getFullYear();

        // const payloadToValidateInV1 = {
        //     cmpstockistcode: invoiceApprovalData?.company_stockist_code,
        //     invoicenumber_searchcolumn: invoiceApprovalData?.invoice?.invoice_number || "",
        //     invoice_accyear: invoiceAccYear,
        //     company_code: invoiceApprovalData?.company_code,
        // };

        // const response = await getInvoiceChangeValidationV1(payloadToValidateInV1);
        // if (response?.data?.success !== "success") {
        //     toast({ description: response?.data?.message, variant: "destructive" });
        //     setIsApproving(false);
        //     return;
        // }
        // const payload = {
        //   data: mappedData,
        //   primary_key: invoiceApprovalData?.invoice?.invoice_id,
        //   event_type: "INVOICE_PRODUCT_EDIT",
        // };

        try {
            // const invoiceApproveResponse = await editInvoiceProduct(payload);
            // if (invoiceApproveResponse) {
            const invoiceEditPayload = {
                data: {
                    invoice_id: invoiceApprovalData?.invoice?.invoice_id,
                    stockist_invoice_attachment_id: Number(stockist_invoice_attachment_id),
                    cash_discount_perc: Number(invoiceApprovalData?.invoice?.cash_discount_perc),
                    cash_discount: String(calculateTotalCashDiscount()),
                    net_amount: String(invoiceApprovalData?.invoice?.net_amount),
                    tcs_perc: Number(tcs),
                    tcs_amt: ((calculateSubtotal() - (calculateSubtotal() * calculateTotalCashDiscount()) / 100 + calculateTotalGst()) * tcs) / 100,
                    gst_amount: calculateTotalGst()?.toFixed(2),
                    gross_amount: calculateSubtotal()?.toFixed(2),
                    approval: true,
                    stockist_id: invoiceApprovalData?.stockist_id,
                    invoice_number: invoiceApprovalData?.invoice?.invoice_number,
                    u_code: invoiceApprovalData?.invoice?.u_code,
                    new_invoice_date: invoiceApprovalData?.invoice?.new_invoice_date,
                    e_inv_ref_type: invoiceApprovalData?.invoice?.e_inv_ref_type,
                    irn: invoiceApprovalData?.invoice?.irn,
                    gst_validated: invoiceApprovalData?.invoice?.gst_validated,
                    dl_validated: invoiceApprovalData?.invoice?.dl_validated,
                    mci_validated: invoiceApprovalData?.invoice?.mci_validated,
                    counter_gst_number: invoiceApprovalData?.counter_gst_number || "",
                    counter_dl_number: invoiceApprovalData?.counter_dl_number || "",
                    counter_mci_number: invoiceApprovalData?.counter_mci_number || "",
                    invoice_num_modified: false,
                    counter_data_validation: false,
                    invoiceProduct: mappedData?.map(item => ({ ...item, temp_invoice_date: tempDate.trim() || invoiceApprovalData?.invoice?.new_invoice_date })),
                },
                primary_key: invoiceApprovalData?.invoice?.invoice_id,
                event_type: "INVOICE_RE_ENTRY",
            };
            console.log(invoiceEditPayload, "re-entry")
            // return
            const invoiceEditResponse = await reEditInvoice(invoiceEditPayload, company_code);

            const invoiceStatusUpdateResponse = await withEventCheck(
                () => {
                    return getInvoiceDetails(stockist_invoice_attachment_id as string, false, company_code || CompanyCode);
                },
                invoiceEditResponse?.data?.data?.products?.at(-1)?.eventStreamCreated?.streamId
            );
            console.log(invoiceStatusUpdateResponse);
            toast({ description: "Invoice approved successfully", variant: "default" });
            navigate("/transactions/invoice/1");
        } catch (error: any) {
            console.log(error);
            toast({ description: error?.response?.data?.message, variant: "destructive" });
        } finally {
            isRequestInProgress.current = false;
            setIsApproving(false);
        }
    };
    const handleApproveInvoice = useDebounceLeading(approveInvoice, 1000);

    const inputFieldValidationHandler = (item: any) => {
        const modifiedValue = Number(item?.modifiedData);

        const priceIdentifier = selectedOrders?.[0]?.price_identifier;
        const discountType = selectedItemDetails?.discount_type;

        if (item?.type === "Quantity") {
            // M10-15357: capacity-aware combined invoice Quantity across every selected batch of
            // this item — folds a Scheme sibling's free qty into its Quantity contribution for a
            // bulk selection (falls back to the plain single-row value otherwise).
            const invoiceValue = combinedInvoiceQuantityCapacity;
            const remainingItem = remainingQuantity?.find((item) => item?.unique_key === (similarOrders?.some((o: any) => o?.batch_number) ? selectedItemDetails?.unique_key?.split("~").slice(0, 2).join("~") : selectedItemDetails?.unique_key?.split("~")[0]));
            const remainingOrderQuantity = remainingItem?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity;
            // const remainingInvoiceQuantity = remainingItem?.remaining_invoice_quantity;
            const remainingInvoiceQuantity = combinedInvoiceUnmappedQuantityCapacity;

            if (Number(modifiedValue) > Number(remainingOrderQuantity)) {
                return true;
            }

            // Inv -> Scheme && Ord -> non-Scheme: the invoiced free qty is folded into Quantity
            // for this combo, so the plain invoice-quantity cap doesn't apply.
            if (
                !(discountType === "Scheme" && priceIdentifier !== 2) && //M10-14122
                (Number(modifiedValue) > Number(invoiceValue) ||
                    Number(modifiedValue) > Number(remainingInvoiceQuantity))
            ) {
                return true;
            }
        } else if (item?.type === "Discount %") {
            const orderedValue = Number(item?.orderedData);
            if (modifiedValue > orderedValue) {
                return true;
            }
        } else if (item?.type === "Scheme Qty") {
            // const invoiceValue = Number(item?.invoiceData);
            // const remainingItem = remainingQuantity?.find((item) => item?.unique_key === (similarOrders?.some((o: any) => o?.batch_number) ? selectedItemDetails?.unique_key?.split("~").slice(0, 2).join("~") : selectedItemDetails?.unique_key?.split("~")[0]));
            // const remainingFreeQuantity = remainingItem?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)
            // ?.remaining_free_quantity;
            // const remainingInvoiceFreeQuantity = remainingItem?.remaining_invoice_free_quantity;
            const allowedFreeQty = item?.allowed_free_qty;
            if (item?.allowed_free_qty > modifiedValue) {
                return true;
            }

            //Inv -> Discount on PTR && Ord -> Scheme
            if (
                (discountType === "Discount Percent" || discountType === "Discount Amount") &&
                Number(selectedItemDetails?.discount_amt) !== 0 &&
                modifiedValue > allowedFreeQty &&
                priceIdentifier === 2
            ) {
                return true;
            }

            //Inv -> Fixed && Ord -> Scheme
            if (
                (discountType === "Discount Percent" || discountType === "Discount Amount") &&
                Number(selectedItemDetails?.discount_amt) == 0 &&
                modifiedValue > allowedFreeQty &&
                priceIdentifier === 2
            ) {
                return true;
            }

            //Inv -> Scheme && Ord -> Discount on PTR
            if (discountType === "Scheme" && modifiedValue > allowedFreeQty && priceIdentifier === 1) {
                return true;
            }

            //Inv -> Fixed && Ord -> Discount on PTR
            if (
                (discountType === "Discount Percent" || discountType === "Discount Amount") &&
                Number(selectedItemDetails?.discount_amt) == 0 &&
                modifiedValue > allowedFreeQty &&
                priceIdentifier === 1
            ) {
                return true;
            }

            //Inv -> Scheme && Ord -> Fixed Price
            if (discountType === "Scheme" && modifiedValue > allowedFreeQty && priceIdentifier === 3) {
                return true;
            }

            //Inv -> Discount on PTR && Ord -> Scheme
            if (
                (discountType === "Discount Percent" || discountType === "Discount Amount") &&
                Number(selectedItemDetails?.discount_amt) !== 0 &&
                modifiedValue > allowedFreeQty &&
                priceIdentifier === 3
            ) {
                return true;
            }

            // if ((!(discountType === "Scheme" && priceIdentifier === 3)) &&
            //   (discountType !== "Discount Percent" && discountType !== "Discount Amount") &&
            //   (!(discountType === "Scheme" && priceIdentifier === 1)) &&
            //   (modifiedValue > invoiceValue || modifiedValue > remainingFreeQuantity || modifiedValue > remainingInvoiceFreeQuantity)
            // ) {
            //   return true;
            // }
        } else if (item?.type === "Price") {
            const invoiceValue = Number(item?.invoiceData);
            const orderedValue = Number(item?.orderedData);

            if ((packEquivalentPrice(selectedItemDetails, selectedOrders?.[0]) ?? Number(selectedOrders?.[0]?.fixed_price)) > modifiedValue && priceIdentifier === 3) {
                return true;
            }
            if (invoiceValue > orderedValue) {
                if (modifiedValue > invoiceValue) {
                    return true;
                }
            } else {
                if (modifiedValue > orderedValue) {
                    return true;
                }
            }

            // M10-14735: Discount % / Scheme Qty modification requires Modified Price to be within 5% of Invoice PTR
            const discountRow = tableData.find((t) => t.type === "Discount %");
            const schemeRow = tableData.find((t) => t.type === "Scheme Qty");
            const ptrRow = tableData.find((t) => t.type === "PTR");
            const discountModified = Number(discountRow?.modifiedData) || 0;
            const schemeModified = Number(schemeRow?.modifiedData) || 0;
            const invoicePtr = Number(ptrRow?.invoiceData);
            const minAcceptablePrice = invoicePtr * 0.95;
            if ((discountModified !== 0 || schemeModified !== 0) && (modifiedValue > invoicePtr || modifiedValue < minAcceptablePrice)) {
                return true;
            }
        } else if (item?.type === "Cash Discount") {
            const orderDiscount = Number(selectedOrders?.[0]?.order?.order_cash_discount_perc);
            const modifyDiscount = Number(item?.modifiedData);
            if (modifyDiscount > orderDiscount) {
                return true;
            }
        }

        return false;
    };

    useEffect(() => {
        fetchInvoiceDetails();
    }, [CompanyCode]);

    const [quantity, setQuantity] = useState<number>(0);
    const [priceList, setPriceList] = useState<any[]>([]);
    const [selectedBatch, setSelectedBatch] = useState<any>({});
    const [price, setPrice] = useState<number>(0);
    const [batchList, setBatchList] = useState<any[]>([]);
    const [gstAmbiguous, setGstAmbiguous] = useState<boolean>(false);
    const [discountPercentage, setDiscountPercentage] = useState<number>(0);
    const [discountAmount, setDiscountAmount] = useState<number>(0);
    const [itemCode, setItemCode] = useState<string>("");

    const [selectedPriceIdentifier, setSelectedPriceIdentifier] = useState<selectedPriceIdentifier>({
        key: "",
        description: "",
        priceldentifier_id: 0,
    });

    const resetFormFields = () => {
        const { reset }: any = smartFormInstance;

        // Reset form fields
        reset();

        // Clear additional state values
        setQuantity(0);
        setPriceList([]);
        setPrice(0);
        setBatchList([]);
        setDiscountPercentage(0);
        setDiscountAmount(0);
        setSelectedPriceIdentifier({
            key: "",
            description: "",
            priceldentifier_id: 0,
        });
        // setFetchTrigger(fetchTrigger + 1);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch both price list and batch list simultaneously
                Promise.all([
                    // M10-12386
                    getInvoicePriceList(itemCode).then((res: any) => {
                        const list = res?.data?.data?.map((item: any) => ({
                            ...item,
                            gst_pc: item?.gst_pc
                                ? `${item?.gst_pc} %`
                                : ""
                        })) || [];

                        setPriceList(list);
                    }),
                    // M10-12386
                    getInvoiceBatchList(itemCode, company_code).then((res: any) => {
                        const apiBatches = res?.data?.data || [];
                        const combinedBatches = [...apiBatches];
                        setBatchList(combinedBatches);
                    }),
                ]);
            } catch (error) {
                console.log(error);
            }
        };
        fetchData()
    }, [itemCode, productsData]);

    const handleAddProduct = async (data: any) => {
        // M10-14735: Free Qty / Discount % / Discount Amount are not allowed when Price is less than PTR
        // if (
        //     !isDiscountEntryAllowedForPtr(price, data?.ptr) &&
        //     (Number(data?.freeQty) > 0 || Number(discountAmount) > 0 || Number(discountPercentage) > 0)
        // ) {
        //     toast({
        //         description: `${selectedPriceIdentifier?.key === "SCHEME"?"Free Qty":"Discount Amount and Discount Percent"} are not allowed when Price is less than PTR`,
        //         variant: "destructive",
        //     });
        //     return;
        // }
        // M10-15166: Price cannot exceed PTR by more than 5%
        if (isPriceExceedingPtr(price, data?.ptr)) {
            toast({
                description: `Price cannot exceed PTR by more than 5%`,
                variant: "destructive",
            });
            return;
        }
        if (Number(data?.freeQty) < 0 || !Number.isInteger(Number(data?.freeQty))) {
            toast({
                description: `Free Quantity can not be decimal or less than 0`,
                variant: "destructive",
            });
            return;
        }
        if ((Number(data?.freeQty) <= 0 || !Number.isInteger(Number(data?.freeQty))) && Number(quantity) <= 0) {
            toast({
                description: `Required Quantity or Free Quantity`,
                variant: "destructive",
            });
            return;
        }
        if (Number(quantity) < 0) {
            toast({
                description: `Quantity cannot be less than or equal to 0`,
                variant: "destructive",
            });
            return;
        }
        if (selectedPriceIdentifier?.key === "SCHEME") {
            if (Number(data?.freeQty) < 0) {
                toast({
                    description: `Free Quantity cannot be less than 0`,
                    variant: "destructive",
                });
                return;
            }
        }
        if (selectedPriceIdentifier?.key === "DISCOUNT_PERCENT" || selectedPriceIdentifier?.key === "DISCOUNT_AMOUNT") {
            if (Number(data?.discountPercentage) < 0 || Number(data?.discountPercentage) > 100) {
                toast({
                    description: `Discount Percentage cannot be less than 0`,
                    variant: "destructive",
                });
                return;
            }
        }
        const newBatchNo = data?.batchNo?.toUpperCase() as string;
        const hasZeroMRP = priceList?.filter(item => parseFloat(item?.mrp) === data?.mrp);
        if (hasZeroMRP.length == 0) {
            toast({
                description: `Incorrect MRP. Please check`,
                variant: "destructive",
            });
            return;
        }
        try {
            const payload = {
                invoice_id: Number(invoiceDetails?.invoice?.invoice_id),
                item_description: String(data?.itemName),
                item_code: String(data?.itemCode),
                unique_key: String(data?.itemCode) + selectedBatch?.batch_no,
                quantity: Number(quantity) || 0,
                gst: data?.gst || "0",
                ptr: Number(data?.ptr) || 0,
                pts: Number(data?.itemPrice?.pts) ? Number(data?.itemPrice?.pts) : Number(data?.pts) || 0, //M10-10856
                discount_type: !selectedPriceIdentifier?.description ? "Discount Amount" : String(selectedPriceIdentifier?.description),
                free_qty: selectedPriceIdentifier?.key === "SCHEME" ? Number(data?.freeQty) : 0,
                batch_no: selectedBatch?.batch_no?.toUpperCase() || newBatchNo,
                inv_rate: String(price) || "0",
                mrp_valid: true,
                inv_mrp: String(data?.mrp) || "0",
                discount_amt: !selectedPriceIdentifier?.description ? "0" : selectedPriceIdentifier?.key === "DISCOUNT_PERCENT" || selectedPriceIdentifier?.key === "DISCOUNT_AMOUNT" ? (isNaN(Number(discountAmount)) ? "0" : String(discountAmount)) : '0',
                discount_perc: data?.discountPercentage,
                counter_code: invoiceDetails?.counter_code,
                is_item_unknown: false,
                gross_amt: (Number(price) * Number(quantity))?.toFixed(2),
                ucode: generateUniqueID(),
            };
            // setMappedData((prev) => [...prev, payload]);
            setProductsData((prev) => [...prev, payload])
            resetFormFields();
            setSelectedBatch({});

            const itemNameField = document.querySelector('input[name="itemName"]');
            if (itemNameField instanceof HTMLElement) {
                itemNameField?.focus();
            }
        } catch (error) {
            console.log(error);
        }
    };
    const smartFormInstance = useForm({
        // @ts-ignore
        resolver: yupResolver(schema),
        mode: "onBlur",
        defaultValues: structuredClone({}),
    });
    const handleAutocompleteChange = async (data: any) => {
        const { setValue }: any = smartFormInstance;
        const value = data;
        if (value) {
            const { isCustom } = value;
            !isCustom && setValue("itemName", value?.product_name);
            !isCustom && setValue("itemCode", value?.item_code);
            // setValue("mrp", value?.mrp || "");
            // setValue("price", value?.ptr || "");
            setPrice(value?.ptr);
            setValue("batchNo", "");
            setValue("discountAmount", "");
            setValue("discountPercentage", "");
            setValue("freeQty", "");
            setValue("quantity", "");
            setValue("schemeType", "");
            setValue("gst", "");
            setValue("itemPrice", "");
            setValue("price", "");
            setValue("ptr", "");
            // setDiscountPercentage(0);
            // setDiscountAmount(0);

            setItemCode(value?.item_code);
        } else {
            // setValue("mrp", "");
            // setValue("price", "");
            setValue("schemeType", "");
            setValue("freeQty", "");
            setValue("discountPercentage", "");
            setValue("discountAmount", "");
            setValue("batchNo", "");
            setValue("itemCode", "");
        }
    };
    const fetchOrderedProductsByItemCode = async (query: string) => {
        try {
            return await getOrderedProducts({
                product_name: "",
                counter_code: invoiceApprovalData?.counter_code,
                item_code: query,
                page: 1,
                page_size: 10,
                company_stockist_code: invoiceApprovalData?.company_stockist_code,
                invoice_date: invoiceApprovalData?.invoice?.new_invoice_date,
                company_code: invoiceApprovalData?.company_code,
            });
        } catch (error) {
            console.log(error);
        }
    };
    const handlePercentageChange = (e?: any) => {
        const { setValue }: any = smartFormInstance;
        const percentage = Number(e) / 100;
        const discountAmount = percentage * quantity * price;
        setDiscountAmount(Number(discountAmount?.toFixed(2)));
        setDiscountPercentage(Number(e));
        setValue("discountAmount", discountAmount?.toFixed(2));
        setValue("discountPercentage", Number(e));
    };
    const handleAmountChange = (e?: any) => {
        const { setValue }: any = smartFormInstance;
        const amount = Number(e);
        const discountPercentage = (amount / (quantity * price)) * 100;
        setDiscountAmount(amount);
        setDiscountPercentage(Number(discountPercentage?.toFixed(2)));
        setValue("discountAmount", amount);
        setValue("discountPercentage", discountPercentage?.toFixed(2));
    };
    const fetchProductsDataForItemCode = async (query: string) => {
        try {
            return await getSimilarCompanyProducts(invoiceDetails?.company_code, "", query, 1, 10);
        } catch (error) {
            console.log(error);
        }
    };
    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Spinner />
            </div>
        );
    }
    return (
        <InvoiceLayout>
            <Fragment>
                <Transition
                    as={Fragment}
                    show={isRemarksShown}
                    enter="transition transition ease-in-out duration-200 transform"
                    enterFrom="translate-x-full"
                    enterTo="translate-x-0"
                    leave="transition  ease-in-out duration-200 transform"
                    leaveFrom="translate-x-0"
                    leaveTo="translate-x-full"
                >
                    <div className="fixed right-0 inset-0 top-0 z-[500]">
                        <InvoiceRemarks invoiceApprovalData={invoiceApprovalData} invoice_id={Number(invoiceApprovalData?.invoice?.invoice_id)} ref={ref} />
                    </div>
                </Transition>
            </Fragment>
            <div className="mx-auto mt-10">
                <div className="flex flex-col px-[3.75rem] mt-6  pb-12 shadow-md border-b-[1px] border-b-[#E9E9E9]">
                    <div className="flex items-center justify-between w-full">
                        <div className="flex flex-col">
                            <div
                                className="flex items-center cursor-pointer font-semibold px-0 py-2 text-lg text-[#7F56D9]"
                                onClick={() => navigate("/transactions/invoice/1")}
                            >
                                <img src={PurpleLeftArrowIcon} alt="back" className="w-3 h-3 mr-3" />
                                Back to Invoices
                            </div>
                            <h1 className="text-[#49484C] font-bold text-[2rem]">Invoice Re-Edit</h1>
                        </div>
                    </div>
                    {/* //M10-11601 */}
                    <div className="absolute right-4 flex flex-row gap-3 items-end mt-24">
                        {/* Versioning */}
                        <div
                            onClick={() => setVersioningModal((prev) => !prev)}
                        >
                            <img src={Book} alt="Version" className="h-10 w-14 " />
                        </div>

                        {/* Remarks */}
                        {viewRemarks && (
                            <div
                                onClick={() => setIsRemarksShown(true)}
                                className="p-2 bg-white cursor-pointer"
                            >
                                <MessageCircle className="text-gray-500 h-6 w-6" />
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-5 mt-10 mx-[3.75rem] rounded-xl border border-[#d0d0d0] shadow-sm">
                    <header className="w-full">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                {invoiceApprovalData?.is_prioritised && (
                                    <img src={star} className="p-1 mr-2 w-8 h-8 rounded-lg border border-[#d0d0d0] shadow-md" alt="star" />
                                )}
                                {!invoiceApprovalData?.is_prioritised && (
                                    <img src={starEmpty} className="p-1 mr-2 w-8 h-8 rounded-lg border border-[#d0d0d0] shadow-md" alt="star" />
                                )}
                                <h1 className={"font-semibold text-lg text-[1.5rem] mr-4"}>
                                    <span className="text-gray-400 text-[1.2rem]">Invoice# </span>
                                    {invoiceApprovalData?.invoice?.invoice_number}
                                </h1>
                                <h1
                                    className={`font-semibold  flex items-center text-xs h-6 px-2 rounded-full`}
                                    style={{
                                        backgroundColor: invoiceApprovalData?.fileStatusHdr?.color_code,
                                        color: invoiceApprovalData?.fileStatusHdr?.text_color_code,
                                    }}
                                >
                                    {invoiceApprovalData?.fileStatusHdr?.fuid_status_desc}
                                    <img className="w-3 h-3 ml-1 cursor-pointer" src={downArrow} alt="down-arrow" />
                                </h1>
                                {invoiceApprovalData?.po_stockist_ack_status === "PENDING" && (
                                  <p className="ml-4 text-[#92400E] text-sm font-semibold border bg-[#FEF3C7] rounded-full px-2 py-0.5">Stockist Ack: Pending</p>
                                )}
                                {invoiceApprovalData?.po_stockist_ack_status === "ACCEPTED" && (
                                  <p className="ml-4 text-[#166534] text-sm font-semibold border bg-[#DCFCE7] rounded-full px-2 py-0.5">Stockist Ack: Accepted</p>
                                )}
                            </div>
                            <div className="flex gap-x-1.5">
                                <Button
                                    onClick={() => {
                                        if (!invoiceApprovalData?.file_path) {
                                            toast({ description: "Invoice not found", variant: "destructive" });
                                            return;
                                        }
                                        downloadInvoice(invoiceApprovalData?.file_path, company_code).then((res: any) => {
                                            if (res?.data?.success && res?.data?.data) {
                                                window.open(res?.data?.data, "_blank"); // Opens the link in a new tab
                                                //M10-15262
                                                dispatch(invoiceActions.setInvoiceVerification({
                                                    invoiceId: invoiceApprovalData?.invoice?.invoice_id,
                                                    isDownloaded: true,
                                                }));
                                            } else {
                                                console.error("Failed to download the invoice.");
                                            }
                                        });
                                    }}
                                    className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                                >
                                    <img src={Download} className="w-4 h-4 mr-1" alt="save-file" />
                                    Invoice{" "}
                                </Button>
                                <Button
                                    onClick={() => {
                                        if (!invoiceApprovalData?.einv_attachment_path) {
                                            toast({ description: "E-Invoice not found", variant: "destructive" });
                                            return;
                                        }
                                        downloadInvoice(invoiceApprovalData?.einv_attachment_path, company_code).then((res: any) => {
                                            if (res?.data?.success && res?.data?.data) {
                                                window.open(res?.data?.data, "_blank"); // Opens the link in a new tab
                                            } else {
                                                console.error("Failed to download the E-Invoice.");
                                            }
                                        });
                                    }}
                                    className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                                >
                                    <img src={Download} className="w-4 h-4 mr-1" alt="send" />
                                    E-Invoice{" "}
                                </Button>
                            </div>
                        </div>
                    </header>
                    {VersioningModal && (
                        <div ref={outSideClickRef}>
                            <VersioningCrate type="Invoice" invoiceId={invoiceApprovalData?.invoice?.invoice_id} stockistInvAttachmentId={invoiceApprovalData?.stockist_invoice_attachment_id} />
                        </div>
                    )}
                    <ul className="mt-5">
                        <li className="my-5 mb-10 rounded-lg">
                            <InvoiceDetailCard
                                isExpandedInitial={true}
                                isExpandable={false}
                                status={invoiceApprovalData?.fileStatusHdr?.fuid_status_desc}
                                checkDataType={false}
                                data={getInvoiceDetailsInfo(invoiceApprovalData)}
                                viewDetails={false}
                                invoiceData={invoiceApprovalData}
                                showHeader={false}
                                fetchInvoiceDetails={fetchInvoiceDetails}
                                tab={1}
                                isMapped={isMapped} //M10-11117
                            />
                        </li>
                    </ul>

                    <div className="p-0 mb-10 border-2 rounded-lg">
                        <div className="m-5">
                            <div className="flex items-center justify-between">
                                <p className="mb-2 text-lg font-semibold">Invoice Entry Details </p>
                                <Button className="w-10 h-10 bg-[#F1F1F1] border border-1-black" onClick={handleToggle}>
                                    <img className={`transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} src={downArrow} alt="down-arrow" />
                                </Button>
                            </div>
                            <div
                                className={`transition-all duration-500 ease-in-out ${isExpanded ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"}`}
                                style={{ transitionProperty: "max-height, opacity" }}
                            >
                                <InvoiceEntryForm
                                    handleAddProduct={handleAddProduct}
                                    smartFormInstance={smartFormInstance}
                                    handleAutocompleteChange={handleAutocompleteChange}
                                    selectedPriceIdentifier={selectedPriceIdentifier}
                                    setSelectedBatch={setSelectedBatch}
                                    schema={schema}
                                    fetchOrderedProductsByItemCode={fetchOrderedProductsByItemCode}
                                    batchList={batchList}
                                    priceList={priceList}
                                    gstAmbiguous={gstAmbiguous}
                                    setGstAmbiguous={setGstAmbiguous}
                                    setPrice={setPrice}
                                    quantity={quantity}
                                    setQuantity={setQuantity}
                                    priceIdentifierList={priceIdentifierList}
                                    setSelectedPriceIdentifier={setSelectedPriceIdentifier}
                                    discountPercentage={discountPercentage}
                                    discountAmount={discountAmount}
                                    handlePercentageChange={handlePercentageChange}
                                    handleAmountChange={handleAmountChange}
                                    useOrderedProducts={"ordered"}
                                    fetchProductsDataForItemCode={fetchProductsDataForItemCode} />
                            </div>
                        </div>
                    </div>

                    {
                        <div className="px-4 mt-10 mb-10 rounded-lg">
                            <div className="flex items-center gap-x-4 pb-5 border-b-2 border-gray-200">
                                <p className="mb-2 text-lg font-semibold">Invoice Details</p>
                                {invoiceApprovalData?.orderCodes?.length > 0 && (
                                    <p className="text-sm text-gray-500 border border-gray-300 rounded-lg p-1 px-2">
                                        Order Code: {invoiceApprovalData?.orderCodes?.map((code: any) => code?.order_code).join(", ")}
                                    </p>
                                )}
                            </div>

                            <table className="min-w-full mt-5 border border-gray-200">
                                <thead className="w-full bg-gray-100">
                                    <tr>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">S.No</th> {/* M10-9670 */}
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Select</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Product Type</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Item code</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Item description</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. MRP</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. PTR</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Price</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. Qty.</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Gross Amt.</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. Scheme</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Discount %</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Discount Amount</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Scheme Qty.</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">CD %</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">GST % </th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Batch</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Order Map</th>
                                        <th className="px-1 py-2 text-sm text-left text-gray-600">Mapped</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...(productsData || [])]?.sort((a: any, b: any) => (a?.textractProduct?.stockistfile_detail_code || 0) - (b?.textractProduct?.stockistfile_detail_code || 0))
                                        ?.map((item: any, index: any) => (
                                            <tr
                                                key={index}
                                                className={`${index % 2 === 0 ? "bg-gray-50 text-sm" : "text-sm"} ${item?.is_item_unknown ? "bg-[#F0F7FF]" : ""}`}
                                            >
                                                <td className="px-1 py-2 border-t">{index + 1}</td> {/* M10-9670 */}
                                                {!item?.is_item_unknown && productsData?.filter((p) => p.item_code === item.item_code).length > 1 ? (
                                                    <td className="px-4 py-2 border-t">
                                                        <Checkbox
                                                            checked={checkedRows.has(getRowId(item, index))}
                                                            disabled={selectedOrders?.[0]?.price_identifier === 2}
                                                            onChange={(e: any) => handleCheckboxChange(e, item, index)}
                                                        />
                                                    </td>
                                                ) : (
                                                    <td className="px-4 py-2 border-t"></td>
                                                )}
                                                <td className="px-1 py-2 border-t">{item?.is_item_unknown ? "UO" : "CP/OP"}</td>
                                                <td className="px-1 py-2 border-t">{item?.item_code}</td>
                                                <td className="px-1 py-2 border-t">{item?.item_description}</td>
                                                <td className="px-1 py-2 border-t">{item?.inv_mrp}</td>
                                                <td className="px-1 py-2 border-t">{item?.ptr}</td>
                                                <td className="px-1 py-2 border-t">{item?.inv_rate}</td>
                                                <td className="px-1 py-2 border-t">{item?.quantity}</td>
                                                <td className="px-1 py-2 border-t">{item?.gross_amt}</td>
                                                <td className="px-1 py-2 border-t">{item?.discount_type}</td>
                                                <td className="px-1 py-2 border-t">{item?.discount_perc}</td>
                                                <td className="px-1 py-2 border-t">{item?.discount_amt}</td>
                                                <td className="px-1 py-2 border-t">{item?.free_qty}</td>
                                                <td className="px-1 py-2 border-t">{item?.p_cash_discount_perc}</td>
                                                <td className="px-1 py-2 border-t">{item?.p_gst_perc}</td>
                                                <td className="px-1 py-2 border-t">{item?.batch_no}</td>
                                                <td className="px-1 py-2 border-t">
                                                    <Button
                                                        onClick={() => handleViewDetails(selectedProducts.length > 0 ? selectedProducts?.[0] : item)}
                                                        className="w-fit h-8 font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0]"
                                                    >
                                                        View
                                                    </Button>
                                                </td>
                                                {
                                                    <td className="px-4 py-2 border-t">
                                                        {item?.mapped && item?.invoice_status === "fully_invoiced" && (
                                                            <Button>
                                                                <img src={SuccessIcon} alt="counter_details_check" className="" />
                                                            </Button>
                                                        )}
                                                        {item?.mapped && item?.invoice_status === "partially_invoiced" && (
                                                            <Button>
                                                                <img src={PartialIcon} alt="counter_details_check" className="" />
                                                            </Button>
                                                        )}
                                                    </td>
                                                }
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    }
                    <div className="mt-10 border border-[#d0d0d0] p-5 rounded-xl mx-4">
                        <div className="flex items-center justify-between">
                            <p className="text-lg font-semibold">{selectedItemDetails?.item_description || "Select an Item"}</p>
                            <div className="flex items-center gap-2">
                                <div className="w-[200px]">
                                    <Autocomplete
                                        openDropdownOnMount={showOrderDropdown}
                                        inputClassName="w-full"
                                        placeholder="Choose an order"
                                        suggestionList={similarOrders?.map((item: any) => ({
                                            id: item?.order?.order_code,
                                            ...item,
                                        }))}
                                        currentSelected={selectedOrders?.[0]?.id || ""}
                                        defaultValue={selectedOrders?.[0]?.id || ""}
                                        uniqueKey="id"
                                        displayKey="id"
                                        onSelectionChange={(value: any) => {
                                            const differentOrder = !invoiceApprovalData?.orderCodes?.some((order: any) => order?.order_code === value?.id);
                                            if (differentOrder) {
                                                setIsDifferentOrderModalOpen(true);
                                                setOrderToSelect([value]);
                                            } else {
                                                setSelectedOrders([value]);
                                                setShowOrderDropdown(false);
                                            }
                                        }}
                                        inputRef={inputRef}
                                    />
                                </div>
                                <div className="p-2 bg-[#F1F1F1] border-2 border-gray-200 rounded-md cursor-pointer" onClick={handleToggle}>
                                    <img className={`transition-transform  duration-300 ${isExpanded ? "rotate-180" : ""}`} src={downArrow} alt="down-arrow" />
                                </div>
                            </div>
                        </div>
                        <div
                            className={`transition-all items-center duration-500 ease-in-out ${isExpanded ? "opacity-100 max-h-[1000px]" : "max-h-0 opacity-0"}`}
                            style={{ transitionProperty: "max-height, opacity" }}
                        >
                            <table className="min-w-full mt-5 border border-gray-200">
                                <thead className="w-full bg-gray-100">
                                    <tr>
                                        <th className="px-4 py-2 text-sm font-normal text-left text-gray-600"></th>
                                        <th className="px-4 py-2 text-sm font-normal text-left text-gray-600">Invoice Data</th>
                                        <th className="px-4 py-2 text-sm font-normal text-left text-gray-600">
                                            Ordered Data - <span className="text-[#8051FF]">{selectedOrders?.[0]?.id}</span>
                                        </th>
                                        <th className="px-4 py-2 text-sm font-normal text-left text-gray-600">Modified Data</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableData.map((item, index) => (
                                        <tr key={index} className="h-12 text-sm">
                                            <td className="px-4 py-2 text-gray-500 border-t">{item?.type}</td>
                                            <td className={`px-4 py-2 border-t ${item.type === "Calculated Value" ? "text-red-600" : ""}`}>
                                                {Number(getInvoiceDataDisplayValue(item))}
                                            </td>
                                            <td className="px-4 py-2 border-t">
                                                {item?.orderedData}{" "}
                                                {selectedOrders?.length > 0 && item?.type === "Quantity" && (
                                                    <span className="text-[#8051FF]">
                                                        (Remaining Qty:{" "}
                                                        {
                                                            remainingQuantity
                                                                ?.find((item) => item?.unique_key === (similarOrders?.some((o: any) => o?.batch_number) ? selectedItemDetails?.unique_key?.split("~").slice(0, 2).join("~") : selectedItemDetails?.unique_key?.split("~")[0]))
                                                                ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_quantity
                                                        }
                                                        )
                                                    </span>
                                                )}
                                                {selectedOrders?.length > 0 && item?.type === "Scheme Qty" && (
                                                    <span className="text-[#8051FF]">
                                                        (Remaining Free Qty:{" "}
                                                        {remainingQuantity
                                                            ?.find((item) => item?.unique_key === (similarOrders?.some((o: any) => o?.batch_number) ? selectedItemDetails?.unique_key?.split("~").slice(0, 2).join("~") : selectedItemDetails?.unique_key?.split("~")[0]))
                                                            ?.orders?.find((order: any) => order?.order_code === selectedOrders?.[0]?.id)?.remaining_free_quantity || 0}
                                                        )
                                                    </span>
                                                )}
                                            </td>
                                            <td className="flex gap-2 px-4 py-2 border-t">
                                                {item.type === "Calculated Value" ? (
                                                    <span className="w-full px-2 py-1 border-black text-red-600">
                                                        {Number(item?.modifiedData)} (
                                                        {(Number(item?.modifiedData) || 0) > 0 ? (((Number(item?.modifiedData) - (Number(item?.invoiceData) || 0)) / Number(item?.modifiedData)) * 100).toFixed(2) : "0.00"}%)
                                                    </span>
                                                ) : (
                                                    <Input
                                                        disabled={item.type === "MRP" || item.type === "PTR"}
                                                        type="number"
                                                        placeholder="0"
                                                        enableArrows={false}
                                                        value={Number(item?.modifiedData)}
                                                        onChange={(e) => {
                                                            handleModifyData(index, e.target.value);
                                                        }}
                                                        onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                                        className="w-full px-2 py-1 border rounded-md border-1-black"
                                                        isError={inputFieldValidationHandler(item)}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-end">
                            {selectedItemDetails && (
                                <Button onClick={() => saveModifiedData(selectedOrders)} className="mt-4 h-9 font-normal text-white bg-[#8051FF] shadow-sm">
                                    Save Modified Data
                                </Button>
                            )}
                        </div>
                    </div>
                </div>


                <div className="mt-10 border mx-[3.75rem] border-[#d0d0d0] p-5 rounded-xl px-10">

                    <div className="flex items-center justify-between pb-5">
                        <p className="text-lg font-semibold">Final Mapped Data</p>
                        <Button
                            disabled={invoiceApprovalData?.fileStatusHdr?.fuid_status_id === 8}
                            className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                            onClick={() => handleReset(invoiceApprovalData)}
                        >
                            Reset
                        </Button>
                    </div>

                    <table className="min-w-full border border-gray-200">
                        <thead className="w-full bg-gray-100">
                            <tr>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">S.No</th> {/* M10-9670 */}
                                <th className="px-1 py-2 text-sm text-left text-gray-600">Item Code</th>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">Order Code</th>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">Item description</th>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. MRP</th>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. PTR</th>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">Price</th>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. Qty.</th>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">Gross Amt.</th>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">Inv. Scheme</th>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">Discount %</th>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">Cash Discount %</th>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">Scheme Qty.</th>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">GST % </th>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">Batch</th>
                                <th className="px-1 py-2 text-sm text-left text-gray-600">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mappedData
                                ?.sort((a: any, b: any) => (a?.textractProduct?.stockistfile_detail_code || 0) - (b?.textractProduct?.stockistfile_detail_code || 0))
                                ?.map((item: any, index: any) => (
                                    <tr
                                        key={index}
                                        className={`text-sm h-12`}
                                        style={{ backgroundColor: uniqueKeyColorMap[item.unique_key?.split("~")?.slice(0, 3)?.join("~")] }}
                                    >
                                        <td className="px-1 py-2 border-t">{index + 1}</td> {/* M10-9670 */}
                                        <td className="px-1 py-2 border-t">{item?.item_code}</td>
                                        <td className="px-1 py-2 border-t">{item?.order_code}</td>
                                        <td className="px-1 py-2 border-t">{item?.item_description}</td>
                                        <td className="px-1 py-2 border-t">{item?.inv_mrp}</td>
                                        <td className="px-1 py-2 border-t">{item?.ptr}</td>
                                        <td className="px-1 py-2 border-t">{item?.inv_rate}</td>
                                        <td className="px-1 py-2 border-t">{item?.quantity}</td>
                                        <td className="px-1 py-2 border-t">{Number(item?.inv_rate * item?.quantity).toFixed(2)}</td>
                                        <td className="px-1 py-2 border-t">{item?.discount_type}</td>
                                        <td className="px-1 py-2 border-t">{Number(item?.discount_perc).toFixed(2)}</td>
                                        <td className="px-1 py-2 border-t">{item?.p_cash_discount_perc}</td>
                                        <td className="px-1 py-2 border-t">{item?.free_qty}</td>
                                        <td className="px-1 py-2 border-t">{item?.p_gst_perc || 0}</td>
                                        <td className="px-1 py-2 border-t">{item?.batch_no}</td>
                                        <td className="px-1 py-2 border-t">
                                            <Button
                                                className="font-normal bg-[#F1F1F1] border-2 border-[#d0d0d0] shadow-sm h-8 focus:outline-none focus:ring-0 hover:text-gray-600"
                                                onClick={() => {
                                                    const modifiedBaseKey = item.unique_key;
                                                    // Filter the mappedData to exclude the current item
                                                    const filteredMappedData = mappedData.filter((product: any) => product?.unique_key !== item?.unique_key);
                                                    // Check if another item with same modifiedBaseKey exists
                                                    const hasSameKeyInMapped = filteredMappedData.some((product: any) => product?.unique_key === modifiedBaseKey);
                                                    // Update productsData only if no same key exists in remaining mappedData

                                                    if (!hasSameKeyInMapped) {
                                                        setProductsData((prev) =>
                                                            prev.map((product) => {
                                                                if (product?.unique_key?.split("~")[0] !== modifiedBaseKey?.split("~")[0]) return product;
                                                                const updatedUnmappedQty = product?.merged
                                                                    ? Number(product?.quantity)
                                                                    : Number(product?.unmappedQty) + Number(item?.quantity);
                                                                const updatedUnmappedFreeQty = product?.merged
                                                                    ? Number(product?.free_qty)
                                                                    : Number(product?.unmappedFreeQty) + Number(item?.free_qty);

                                                                let status = "fully_invoiced";

                                                                if (updatedUnmappedQty === Number(product.quantity) && updatedUnmappedFreeQty === Number(product.free_qty)) {
                                                                    status = "not_invoiced";
                                                                } else if (updatedUnmappedQty < product.quantity || updatedUnmappedFreeQty < product.free_qty) {
                                                                    status = "partially_invoiced";
                                                                }

                                                                return {
                                                                    ...product,
                                                                    mapped: status === "not_invoiced" ? false : true,
                                                                    unmappedQty: updatedUnmappedQty,
                                                                    unmappedFreeQty: updatedUnmappedFreeQty,
                                                                    invoice_status: status,
                                                                };
                                                            })
                                                        );
                                                    }

                                                    // Remove the item from mappedData
                                                    setMappedData((prev: any[]) => prev?.filter((product: any) => product?.unique_key !== item?.unique_key));
                                                    // Update remaining quantity
                                                    setRemainingQuantity((prev: any[]) =>
                                                        prev.map((product: any) => {
                                                            // handleViewDetails keys the entry by `itemCode~batchNo` when
                                                            // similar orders carry batch numbers, otherwise by `itemCode`.
                                                            // similarOrders is cleared by the time Delete runs, so match
                                                            // against either format to ensure the decrement is reversed.
                                                            const fullKey = item.unique_key?.split("~").slice(0, 2).join("~");
                                                            const baseKey = item.unique_key?.split("~")[0]; //M10-14279

                                                            return product?.unique_key === fullKey || product?.unique_key === baseKey //M10-14279
                                                                ? {
                                                                    ...product,

                                                                    orders:
                                                                        product?.orders?.map((order: any) =>
                                                                            order?.order_code === item?.order_code?.[0]
                                                                                ? {
                                                                                    ...order,
                                                                                    remaining_quantity: order?.remaining_quantity + item?.quantity,
                                                                                    remaining_free_quantity: order?.remaining_free_quantity + item?.free_qty,
                                                                                }
                                                                                : order
                                                                        ) ?? [],
                                                                    remaining_invoice_quantity: product?.remaining_invoice_quantity + item?.quantity,
                                                                    remaining_invoice_free_quantity: product?.remaining_invoice_free_quantity + item?.free_ty,
                                                                }
                                                                : product;
                                                        })
                                                    );
                                                    //M10-11117
                                                    if (mappedData.length == 1) {
                                                        setIsMapped(true)
                                                    }
                                                    //M10-11117
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>

                <InvoiceApprovalOrderedData orderData={orderedData} invoiceData={invoiceApprovalData}/>
                <div className="flex justify-end">
                    <div className="p-6 mt-10 mr-9 rounded-xl w-[360px]">
                        <Button
                            disabled={isRequestInProgress.current}
                            loaderSize="small"
                            isLoading={isApproving}
                            className="w-full h-9 mt-5 font-normal text-white bg-[#8051FF] shadow-sm"
                            onClick={() => {
                                if (mappedData?.length == 0) {
                                    toast({ description: "Please add products before approval", variant: "destructive" });
                                    return;
                                }
                                handleApproveInvoice();
                            }}
                            module={fineGrainedPermissionsKeys.INVOICE["UPDATE/APPROVEINVOICE"].module}
                            action={fineGrainedPermissionsKeys.INVOICE["UPDATE/APPROVEINVOICE"].action}
                        >
                            Approve Invoice
                        </Button>
                    </div>
                </div>

                {/* <div className="p-6 mt-10 rounded-xl border border-[#d0d0d0] w-[360px] shadow-sm mx-[3.75rem]">
                    <p className="mb-2 text-lg font-semibold">Calculations</p>
                    <div className="flex flex-col pb-10 gap-y-1">
                        <p className="flex justify-between py-2 text-sm">
                            Entry Net Amount <span className="">₹ {Number(entryNetAmt).toLocaleString("en-IN")}</span>
                        </p>
                        <p className="flex justify-between py-2 text-sm">
                            Subtotal{" "}
                            <span className="">
                                ₹ {orderedData?.length > 0 ? invoiceApprovalData?.invoice?.gross_amount : Number(calculateSubtotal().toFixed(2))}
                            </span>
                        </p>
                        <p className="flex justify-between py-2 text-sm">
                            <span className="mr-2">Cash Discount %</span>
                            <div className="">
                                <Input
                                    type="number"
                                    enableArrows={false}
                                    showIcon={false}
                                    disabled={true}
                                    placeholder="0"
                                    value={
                                        orderedData?.length > 0 ? invoiceApprovalData?.invoice?.cash_discount_perc : calculateTotalCashDiscountPercentage()?.toFixed(2)
                                    }
                                    className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                                />
                            </div>
                        </p>
                        <p className="flex justify-between py-2 text-sm">
                            <span className="mr-2">Cash Discount Amount</span>
                            <div className="">
                                <Input
                                    type="number"
                                    enableArrows={false}
                                    showIcon={false}
                                    disabled={true}
                                    value={orderedData?.length > 0 ? invoiceApprovalData?.invoice?.cash_discount : calculateTotalCashDiscount()?.toFixed(2)}
                                    className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                                />
                            </div>
                        </p>
                        <p className="flex justify-between py-2 text-sm">
                            GST Amount
                            <span className="font-semibold">
                                ₹{orderedData?.length > 0 ? invoiceApprovalData?.invoice?.gst_amount : calculateTotalGst()?.toFixed(2)}
                            </span>
                        </p>
                        <p className="flex justify-between py-2 text-sm">
                            <span className="mr-2">TCS %</span>
                            <div className="">
                                <Input
                                    enableArrows={false}
                                    showIcon={false}
                                    type="number"
                                    placeholder="TCS"
                                    value={orderedData?.length > 0 ? invoiceApprovalData?.invoice?.tcs_perc : tcs}
                                    // value={Number(invoiceApprovalData?.invoice?.tcs_perc)}
                                    onChange={(e: any) => setTcs(e?.target?.value)}
                                    className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                                />{" "}
                            </div>
                        </p>
                        <p className="flex justify-between py-2 text-sm">
                            Total (as calculated){" "}
                            <span className="font-semibold">
                                ₹{" "}
                                {
                                    calculateTotalAsFinal() ? Number(calculateTotalAsFinal().toFixed(2)) : Number(calculateTotal().toFixed(2))}
                            </span>
                        </p>
                        <Button
                            disabled={isRequestInProgress.current}
                            loaderSize="small"
                            isLoading={isApproving}
                            className="w-full h-9 mt-5 font-normal text-white bg-[#8051FF] shadow-sm"
                            onClick={() => {
                                if (mappedData?.length == 0) {
                                    toast({ description: "Please add products before approval", variant: "destructive" });
                                    return;
                                }
                                if (Math.abs(Number(entryNetAmt) - Number(calculateTotal())) > 10) {
                                    setOpenNetAmtModal(true);
                                } else {
                                    handleApproveInvoice();
                                }
                            }}
                            module={fineGrainedPermissionsKeys.INVOICE["UPDATE/APPROVEINVOICE"].module}
                            action={fineGrainedPermissionsKeys.INVOICE["UPDATE/APPROVEINVOICE"].action}
                        >
                            Approve Invoice
                        </Button>
                    </div>
                </div> */}
            </div>
            <Modal isOpen={isDifferentOrderModalOpen} isHeader={false} onCloseModal={() => setIsDifferentOrderModalOpen(false)} title="Is Different Order">
                <IsDifferentOrder
                    handleApproveInvoice={() => {
                        setSelectedOrders(orderToSelect);
                        setShowOrderDropdown(false);
                        setIsDifferentOrderModalOpen(false);
                    }}
                    onCloseModal={() => {
                        setOrderToSelect([]);
                        setIsDifferentOrderModalOpen(false);
                    }}
                    invoiceOrderCode={invoiceApprovalData?.orderCodes?.map((order: any) => order?.order_code)?.join(",")}
                    selectedOrderCode={orderToSelect?.[0]?.id}
                />
            </Modal>
            <Modal isOpen={openPerStripModal} isHeader={false} onCloseModal={() => resumePerStripSave(false)} title="Order Pack Change">
              <PerStripOrderEdit
                message={packChangeMessage}
                onCloseModal={() => resumePerStripSave(false)}
                onConfirm={() => resumePerStripSave(true)}
                onDecline={() => resumePerStripSave(false)}
              />
            </Modal>
        </InvoiceLayout>
    );
};
// M10-12366